import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  Container,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Button,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputBase,
  FormControlLabel,
  Checkbox,
  TextareaAutosize,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import {
  DPRFormData,
  DPRPurpose,
  TargetEntry,
  IndicatorType,
  INDICATOR_ACTIVITIES,
} from "src/types/dpr.types";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { getProjectById } from "src/utils/api/dashboard";
import {
  submitDpr,
  getDprByEpid,
  getDistinctUnits,
  reviewDpr,
  getDprQueries,
} from "src/utils/api/dpr";
import { transformDprFormToPayload } from "src/utils/transformers/dprFormTransformer";
import { fileUpload } from "src/utils/api/fileupload";
import { AxiosError, AxiosResponse } from "axios";
import MapComponent from "src/components/common/KML";
import StatusHistory from "src/components/common/StatusHistory";
import { useAppSelector } from "src/redux/store";

// Component Styles
const styles = {
  container: {
    py: 3,
    backgroundColor: "#F4F5F7",
    minHeight: "100vh",
  },
  stepper: {
    backgroundColor: "#FDFDFD",
    mb: 4,
    borderRadius: "8px",
    p: 2,
  },
  card: {
    backgroundColor: "#FDFDFD",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    mb: 4,
  },
  sectionHeader: {
    backgroundColor: "#36B1A0",
    color: "#FDFDFD",
    p: 3,
    borderRadius: "8px 8px 0 0",
  },
  sectionTitle: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: "#FDFDFD",
  },
  sectionContent: {
    p: 3,
  },
  fieldLabel: {
    fontSize: "0.95rem",
    fontWeight: 600,
    color: "#1F2937",
    mb: 1,
    display: "block",
  },
  inputField: {
    "& .MuiOutlinedInput-root": {
      borderColor: "#D1D5DB",
      "&:hover fieldset": {
        borderColor: "#36B1A0",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#36B1A0",
      },
    },
  },
  button: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: "6px",
    py: 1.2,
    px: 3,
  },
  primaryButton: {
    backgroundColor: "#36B1A0",
    color: "#FDFDFD",
    "&:hover": {
      backgroundColor: "#2d8f80",
    },
  },
  secondaryButton: {
    color: "#36B1A0",
    borderColor: "#36B1A0",
    "&:hover": {
      backgroundColor: "rgba(54, 177, 160, 0.05)",
    },
  },
  addButton: {
    backgroundColor: "#36B1A0",
    color: "#FDFDFD",
  },
  tableHeader: {
    backgroundColor: "#36B1A0",
    "& th": {
      color: "#FDFDFD",
      fontWeight: 700,
    },
  },
  dropzone: {
    border: "2px dashed #36B1A0",
    borderRadius: "8px",
    p: 4,
    textAlign: "center",
    cursor: "pointer",
    backgroundColor: "rgba(54, 177, 160, 0.05)",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(54, 177, 160, 0.1)",
      borderColor: "#2d8f80",
    },
  },
};

// PDF file paths (served from the portal's public/ folder)
const DPR_FORMAT_PDF = "/DPR-Format.pdf";
const MODEL_DPR_PDF = "/Modal-DPR.pdf";

const downloadPdf = (filePath: string, fileName: string) => {
  const link = document.createElement("a");
  link.href = process.env.PUBLIC_URL + encodeURI(filePath);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Main DPR Form Component
const DPRForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const landOwnershipEpid = searchParams.get("landOwnershipEpid") || "";

  const [activeStep, setActiveStep] = useState(0);
  const [projectId, setProjectId] = useState<string>("");
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "info" });
  const [formData, setFormData] = useState<Partial<DPRFormData>>({
    projectName: "",
    projectType: undefined,
    gcaName: "",
    gcaType: undefined,
    gcaId: "",
    contactPerson: "",
    contactEmail: "",
    contactPhone: "",
    landParcelId: "",
    landParcelName: "",
    landArea: 0,
    areaUnit: "ha",
    state: "",
    district: "",
    block: "",
    village: "",
    circle: "",
    division: "",
    range: "",
    acknowledgementDate: "",
    registrationNumber: "",
    gcaAddress: "",
    gcaEmail: "",
    gcaPhone: "",
    contactPersonDesignation: "",
    contactPersonAddress: "",
    stateNodalOfficerId: "",
    stateNodalOfficerName: "",
    stateNodalOfficerDesignation: "",
    stateNodalOfficerContact: "",
    districtNodalOfficerId: "",
    districtNodalOfficerName: "",
    districtNodalOfficerDesignation: "",
    districtNodalOfficerContact: "",
    dprPurpose: [],
    estimatedGreenCredits: undefined,
    greenCreditUsageProjects: "",
    // Part B - Site Characteristics
    landUseCategory: "",
    landLegalStatus: "",
    topography: undefined,
    slopePercentage: undefined,
    nearestRoadType: "",
    distanceFromRoad: undefined,
    distanceFromHabitation: undefined,
    approachRoute: "",
    climaticZone: "",
    inspectionPaths: "",
    soilType: undefined,
    soilDepthTexture: "",
    soilMoistureRegime: undefined,
    soilPH: undefined,
    soilFertilityStatus: "",
    waterSource: undefined,
    waterSupplyLines: "",
    distanceToNearestWaterSource: undefined,
    suitabilityForConservation: "",
    vegetationType: undefined,
    dominantSpecies: "",
    canopyDensity: undefined,
    treesGirth0to10: undefined,
    treesGirth10to30: undefined,
    treesGirth30to60: undefined,
    treesGirthGreater60: undefined,
    invasiveSpeciesPresent: "",
    invasiveSpeciesDetails: "",
    existingFauna: "",
    proximityToLocalCommunities: undefined,
    nearbySttlements: "",
    rightsAndUsage: "",
    communityDependency: "",
    existingRestrictions: "",
    // Part C - Interventions & Zoning
    interventionType: undefined,
    treePlantationArea: undefined,
    treePlantationSpecies: "",
    seedlingsPerHectare: undefined,
    spacingPattern: "",
    nurserySource: undefined,
    nurseryLocation: "",
    temporaryNurseryDetails: "",
    maintenancePlan: "",
    treeSpecies: [],
    soilConservationArea: undefined,
    conservationWorkType: "",
    conservationWorkZone: "",
    conservationWorkPurpose: "",
    waterSupplyArea: undefined,
    proposedWaterSource: "",
    supplyLineLayout: "",
    waterHarvestingStructures: "",
    protectionArea: undefined,
    fencingType: undefined,
    fencingLength: undefined,
    firePreventionWorks: "",
    watchWardArrangements: "",
    biodiversityArea: undefined,
    pollinatorSpecies: "",
    ntfpSpecies: "",
    grassesShrubsMedicinal: "",
    // Implementation Timeline (Year 1-10 - Targets with indicator, activity, unit, numberOfUnits, cost)
    yearwiseTargetsData1: [],
    yearwiseTargetsData2: [],
    yearwiseTargetsData3: [],
    yearwiseTargetsData4: [],
    yearwiseTargetsData5: [],
    yearwiseTargetsData6: [],
    yearwiseTargetsData7: [],
    yearwiseTargetsData8: [],
    yearwiseTargetsData9: [],
    yearwiseTargetsData10: [],
    // Part D - Declarations and Undertakings
    declarationTrueComplete: false,
    declarationAbideRules: false,
    declarationAdequateResources: false,
    declarationMaintenanceFund: false,
    declarationPerformanceParameters: false,
    declarationProgressReports: false,
    declarationNoLandClaim: false,
    declarationNoCommercialActivity: false,
  });
  const [projectData, setProjectData] = useState<any>(null);
  const [attemptedSteps, setAttemptedSteps] = useState<Set<number>>(new Set());

  const [kmlFile, setKmlFile] = useState<File | null>(null);
  const [kmlDocumentUrl, setKmlDocumentUrl] = useState<string | null>(null);
  const [interventionKmlUrl, setInterventionKmlUrl] = useState<string | null>(
    null
  );
  const [unitOptions, setUnitOptions] = useState<string[]>([]);
  const [landOwnershipStatus, setLandOwnershipStatus] = useState<string | null>(
    null
  );
  const [dprStatus, setDprStatus] = useState<string | null>(null);
  const [dprVersionId, setDprVersionId] = useState<string | null>(null);
  const [queryDialogOpen, setQueryDialogOpen] = useState(false);
  const [queryText, setQueryText] = useState("");
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approveReason, setApproveReason] = useState("");
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [submitRemark, setSubmitRemark] = useState("");
  // Blocks a second submit in the gap between the mutation succeeding and the
  // refetched dprStatus hiding the button.
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ open: false, title: "", message: "", onConfirm: () => {} });

  const user = useAppSelector((state) => state.root.user);
  const userType = user?.data?.userType;

  // User type detection (same as Header component)
  // SNO (Nodal Officer): userType === "INDIVIDUAL"
  // IO/DNO (Implementing Officer): !userType (null/empty)
  // Entity: userType === "ORGANIZATION"
  const isSNO = userType === "INDIVIDUAL";
  const isDNO = !userType;
  const isEntity = userType === "ORGANIZATION";

  // Edit permission: Only Entity can edit DPR, and only in these dprStatus values
  const canEditDpr =
    isEntity &&
    (!dprStatus ||
      dprStatus === "DPR_DRAFT" ||
      dprStatus === "DPR_DNO_QUERY" ||
      dprStatus === "DPR_SNO_QUERY" ||
      dprStatus === "DPR_ADMIN_QUERY" ||
      dprStatus === "DPR_REJECTED");

  // Declarations editable only for Entity with editable dprStatus values
  const canEditDeclarations =
    isEntity &&
    (!dprStatus ||
      dprStatus === "DPR_DRAFT" ||
      dprStatus === "DPR_DNO_QUERY" ||
      dprStatus === "DPR_SNO_QUERY" ||
      dprStatus === "DPR_ADMIN_QUERY" ||
      dprStatus === "DPR_REJECTED");

  // DPR_SUBMIT or DPR_SNO_QUERY → IO/DNO can approve or query
  // DPR_UNDER_SNO or DPR_ADMIN_QUERY → SNO can approve or query
  const showReviewActions =
    (isDNO && (dprStatus === "DPR_SUBMIT" || dprStatus === "DPR_SNO_QUERY")) ||
    (isSNO &&
      (dprStatus === "DPR_UNDER_SNO" || dprStatus === "DPR_ADMIN_QUERY"));

  const { mutate: reviewMutate, isLoading: isReviewLoading } = useMutation(
    "reviewDpr",
    (payload: {
      epid: string;
      action: "APPROVE" | "QUERY" | "REJECT";
      query?: string;
      reason?: string;
    }) => reviewDpr(payload),
    {
      onSuccess: () => {
        setQueryDialogOpen(false);
        setQueryText("");
        setApproveDialogOpen(false);
        setApproveReason("");
        setSnackbar({
          open: true,
          message: "Review action submitted successfully",
          severity: "success",
        });
        // Refresh component data to reflect new status
        queryClient.invalidateQueries(["dpr", landOwnershipEpid]);
        queryClient.invalidateQueries(["dprQueries", landOwnershipEpid]);
      },
      onError: () => {
        setSnackbar({
          open: true,
          message: "Failed to submit review action",
          severity: "error",
        });
      },
    }
  );

  // Fetch DPR queries history
  // refetchOnMount/staleTime override the app-wide refetchOnMount:false default
  const { data: dprQueriesRes } = useQuery(
    ["dprQueries", landOwnershipEpid],
    () => getDprQueries(landOwnershipEpid),
    { enabled: !!landOwnershipEpid, refetchOnMount: "always", staleTime: 0 }
  );

  // Helper to get user type label
  const getUserTypeLabel = (type: string | null) => {
    if (type === "INDIVIDUAL") return "Nodal Officer";
    if (type === "ORGANIZATION") return "Entity";
    return "Implementing Officer";
  };

  const dprQueryHistory = useMemo(
    () =>
      (dprQueriesRes?.data ?? []).map((q: any, index: number) => ({
        id: q.id || `dpr-query-${index}`,
        status: q.newStatus ?? null,
        oldStatus: q.oldStatus ?? null,
        changedByRole: getUserTypeLabel(q.userType),
        query: q.query,
        created_at: q.createdAt,
        user: { firstName: q.userName },
      })),
    [dprQueriesRes]
  );

  const queryClient = useQueryClient();

  // Fetch existing DPR data by EPID (also resolves projectDetailId)
  const { isLoading: isDprLoading } = useQuery(
    ["dpr", landOwnershipEpid],
    () => getDprByEpid(landOwnershipEpid),
    {
      enabled: !!landOwnershipEpid,
      onSuccess: (res: AxiosResponse) => {
        const dprData = res.data;
        // Store projectDetailId resolved from backend
        if (dprData?.projectDetailId && !projectId) {
          setProjectId(dprData.projectDetailId);
        }
        // Store land ownership status and dpr status
        if (dprData?.landOwnershipStatus) {
          setLandOwnershipStatus(dprData.landOwnershipStatus);
        }
        if (dprData?.dprStatus) {
          setDprStatus(dprData.dprStatus);
        }
        if (dprData?.dprVersionId) {
          setDprVersionId(dprData.dprVersionId);
        }
        {
          // Pre-fill declarations for statuses beyond null / DPR_DRAFT
          const currentDprStatus = dprData?.dprStatus;
          if (currentDprStatus && currentDprStatus !== "DPR_DRAFT") {
            setFormData((prev) => ({
              ...prev,
              declarationTrueComplete: true,
              declarationAbideRules: true,
              declarationAdequateResources: true,
              declarationMaintenanceFund: true,
              declarationPerformanceParameters: true,
              declarationProgressReports: true,
              declarationNoLandClaim: true,
              declarationNoCommercialActivity: true,
            }));
          }
        }
        if (dprData?.intervention) {
          const intv = dprData.intervention;
          setFormData((prev) => ({
            ...prev,
            interventionType: intv.interventionType || "",
            kmlFileId: intv.kmlFileId || "",
            kmlFileName: intv.kmlFileName || "",
            treePlantationArea: intv.treePlantationArea,
            treePlantationSpecies: intv.treePlantationSpecies || "",
            seedlingsPerHectare: intv.seedlingsPerHectare,
            spacingPattern: intv.spacingPattern || "",
            nurserySource: intv.nurserySource || "",
            nurseryLocation: intv.nurseryLocation || "",
            temporaryNurseryDetails: intv.temporaryNurseryDetails || "",
            maintenancePlan: intv.maintenancePlan || "",
            treeSpecies: intv.treeSpecies || [],
            soilConservationArea: intv.soilConservationArea,
            conservationWorkType: intv.conservationWorkType || "",
            conservationWorkZone: intv.conservationWorkZone || "",
            conservationWorkPurpose: intv.conservationWorkPurpose || "",
            waterSupplyArea: intv.waterSupplyArea,
            proposedWaterSource: intv.proposedWaterSource || "",
            supplyLineLayout: intv.supplyLineLayout || "",
            waterHarvestingStructures: intv.waterHarvestingStructures || "",
            protectionArea: intv.protectionArea,
            fencingType: intv.fencingType || "",
            fencingLength: intv.fencingLength,
            firePreventionWorks: intv.firePreventionWorks || "",
            watchWardArrangements: intv.watchWardArrangements || "",
            biodiversityArea: intv.biodiversityArea,
            pollinatorSpecies: intv.pollinatorSpecies || "",
            ntfpSpecies: intv.ntfpSpecies || "",
            grassesShrubsMedicinal: intv.grassesShrubsMedicinal || "",
            dprPurpose: intv.dprPurpose || [],
            estimatedGreenCredits: intv.estimatedGreenCredits,
            greenCreditUsageProjects: intv.gcUtilisationDescription || "",
            // Store user IDs for GCA / SNO / DNO
            gcaId: intv.gcaUserId || prev.gcaId || "",
            stateNodalOfficerId:
              intv.snoUserId || prev.stateNodalOfficerId || "",
            districtNodalOfficerId:
              intv.dnoUserId || prev.districtNodalOfficerId || "",
            // Project / Location Info
            projectName: intv.projectName || prev.projectName || "",
            projectType: intv.projectType || prev.projectType || "",
            landParcelId: intv.landParcelId || prev.landParcelId || "",
            landParcelName: intv.landParcelName || prev.landParcelName || "",
            state: intv.state || prev.state || "",
            district: intv.district || prev.district || "",
            block: intv.block || prev.block || "",
            village: intv.village || prev.village || "",
            division: intv.division || prev.division || "",
            range: intv.rangeName || prev.range || "",
            nearbySttlements:
              intv.nearbySettlements || prev.nearbySttlements || "",
            distanceFromHabitation:
              intv.distanceFromHabitation || prev.distanceFromHabitation || "",
            areaUnit: intv.areaUnit || prev.areaUnit || "ha",
            // Declarations
            declarationTrueComplete:
              intv.declarationTrueComplete ??
              prev.declarationTrueComplete ??
              false,
            declarationAbideRules:
              intv.declarationAbideRules ?? prev.declarationAbideRules ?? false,
            declarationAdequateResources:
              intv.declarationAdequateResources ??
              prev.declarationAdequateResources ??
              false,
            declarationMaintenanceFund:
              intv.declarationMaintenanceFund ??
              prev.declarationMaintenanceFund ??
              false,
            declarationPerformanceParameters:
              intv.declarationPerformanceParameters ??
              prev.declarationPerformanceParameters ??
              false,
            declarationProgressReports:
              intv.declarationProgressReports ??
              prev.declarationProgressReports ??
              false,
            declarationNoLandClaim:
              intv.declarationNoLandClaim ??
              prev.declarationNoLandClaim ??
              false,
            declarationNoCommercialActivity:
              intv.declarationNoCommercialActivity ??
              prev.declarationNoCommercialActivity ??
              false,
          }));

          // Use KML file URL resolved by backend
          if (intv.kmlFileUrl) {
            setInterventionKmlUrl(intv.kmlFileUrl);
          }
        }
        // Populate DPR-level landArea, landUseCategory, acknowledgementDate from intervention
        if (dprData?.dprLandArea) {
          setFormData((prev) => ({
            ...prev,
            landArea: dprData.dprLandArea,
          }));
        }
        if (dprData?.dprLandUseCategory) {
          setFormData((prev) => ({
            ...prev,
            landUseCategory: dprData.dprLandUseCategory,
          }));
        }
        if (dprData?.dprAcknowledgementDate) {
          setFormData((prev) => ({
            ...prev,
            acknowledgementDate: dprData.dprAcknowledgementDate,
          }));
        }
        // Populate ALL site characteristic fields from DPR response
        // When intervention exists, backend merges intervention site chars into landSpecification
        if (dprData?.landSpecification) {
          const ls = dprData.landSpecification;
          setFormData((prev) => ({
            ...prev,
            landLegalStatus: ls.legalStatusOfLand || prev.landLegalStatus || "",
            topography: ls.terrain || prev.topography || "",
            slopePercentage: ls.slope
              ? parseFloat(ls.slope)
              : prev.slopePercentage,
            climaticZone: ls.climaticZone || prev.climaticZone || "",
            soilType: ls.soilType || prev.soilType || "",
            soilDepthTexture:
              ls.soilDepthAndTexture || prev.soilDepthTexture || "",
            soilMoistureRegime:
              ls.soilMoistureRegime || prev.soilMoistureRegime || "",
            soilPH: ls.soilpH || prev.soilPH || "",
            waterSource: ls.sourceOfWater || prev.waterSource || "",
            waterSupplyLines:
              ls.waterSupplyLines || prev.waterSupplyLines || "",
            distanceToNearestWaterSource:
              ls.distanceToNearestWaterSource ||
              prev.distanceToNearestWaterSource ||
              "",
            vegetationType: ls.typeOfVegetation || prev.vegetationType || "",
            dominantSpecies: ls.dominantSpecies || prev.dominantSpecies || "",
            canopyDensity: ls.treeCanopyDensity
              ? parseFloat(ls.treeCanopyDensity)
              : prev.canopyDensity,
            invasiveSpeciesPresent:
              ls.presenceOfInvasiveSpecies || prev.invasiveSpeciesPresent || "",
            invasiveSpeciesDetails:
              ls.invasiveSpeciesDetails || prev.invasiveSpeciesDetails || "",
            existingFauna: ls.existingFauna || prev.existingFauna || "",
            proximityToLocalCommunities:
              ls.proximityToLocalCommunities ||
              prev.proximityToLocalCommunities ||
              "",
            rightsAndUsage: ls.rightsAndUsage || prev.rightsAndUsage || "",
            communityDependency:
              ls.communityDependency || prev.communityDependency || "",
            existingRestrictions:
              ls.anyExistingRestrictions || prev.existingRestrictions || "",
            distanceFromRoad: ls.accessibility || prev.distanceFromRoad || "",
            circle: ls.circleName || prev.circle || "",
            nearestRoadType: ls.nearestRoadType || prev.nearestRoadType || "",
            approachRoute: ls.approachRoute || prev.approachRoute || "",
            inspectionPaths: ls.inspectionPaths || prev.inspectionPaths || "",
            soilFertilityStatus:
              ls.soilFertilityStatus || prev.soilFertilityStatus || "",
            suitabilityForConservation:
              ls.suitabilityForConservation ||
              prev.suitabilityForConservation ||
              "",
            treesGirth0to10: ls.treesGirth0To10 ?? prev.treesGirth0to10,
            treesGirth10to30: ls.treesGirth10To30 ?? prev.treesGirth10to30,
            treesGirth30to60: ls.treesGirth30To60 ?? prev.treesGirth30to60,
            treesGirthGreater60:
              ls.treesGirthGreater60 ?? prev.treesGirthGreater60,
          }));
        }
        // Populate year-wise targets from DPR response
        if (dprData?.targets) {
          const yearToFieldMap: Record<string, string> = {
            "1st Year": "yearwiseTargetsData1",
            "2nd Year": "yearwiseTargetsData2",
            "3rd Year": "yearwiseTargetsData3",
            "4th Year": "yearwiseTargetsData4",
            "5th Year": "yearwiseTargetsData5",
            "6th Year": "yearwiseTargetsData6",
            "7th Year": "yearwiseTargetsData7",
            "8th Year": "yearwiseTargetsData8",
            "9th Year": "yearwiseTargetsData9",
            "10th Year": "yearwiseTargetsData10",
          };
          const targetUpdates: Partial<DPRFormData> = {};
          for (const [year, fieldName] of Object.entries(yearToFieldMap)) {
            const yearTargets = dprData.targets[year];
            if (yearTargets?.length) {
              (targetUpdates as any)[fieldName] = yearTargets.map(
                (t: any): TargetEntry => ({
                  id: t.id || `target-${Date.now()}-${Math.random()}`,
                  indicator: t.indicator || "",
                  activity: t.activity || "",
                  unit: t.unit || "",
                  numberOfUnits: t.numberOfUnits,
                  costPerUnit: t.costPerUnit,
                  costRequired: t.costRequired,
                  description: t.description || "",
                })
              );
            }
          }
          if (Object.keys(targetUpdates).length > 0) {
            setFormData((prev) => ({ ...prev, ...targetUpdates }));
          }
        }
        // Populate GCA Details from intervention FK → gcaUser, fallback to land_ownership user
        const gcaSource = dprData?.gcaUser || dprData?.user;
        if (gcaSource) {
          const user = gcaSource;
          const userAddress = user.address || {};
          const fullName = [user.firstName, user.lastName]
            .filter(Boolean)
            .join(" ");
          const addressParts = [
            userAddress.addressLine1,
            userAddress.addressLine2,
            userAddress.city,
            userAddress.village,
          ].filter(Boolean);
          setFormData((prev) => ({
            ...prev,
            gcaId: user.id || prev.gcaId || "",
            gcaName: fullName || prev.gcaName || "",
            gcaType: user.userType || prev.gcaType || "",
            gcaEmail: user.email || prev.gcaEmail || "",
            gcaPhone: user.phoneNumber || prev.gcaPhone || "",
            gcaAddress: addressParts.join(", ") || prev.gcaAddress || "",
            contactPerson:
              user.nameOfAuthorizedPerson || prev.contactPerson || "",
            contactPersonDesignation:
              user.designation || prev.contactPersonDesignation || "",
          }));
        }
        // Populate SNO Details from intervention FK → snoUser
        if (dprData?.snoUser) {
          const sno = dprData.snoUser;
          setFormData((prev) => ({
            ...prev,
            stateNodalOfficerId: sno.id || prev.stateNodalOfficerId || "",
            stateNodalOfficerName:
              [sno.firstName, sno.lastName].filter(Boolean).join(" ") ||
              prev.stateNodalOfficerName ||
              "",
            stateNodalOfficerDesignation:
              sno.division || prev.stateNodalOfficerDesignation || "",
            stateNodalOfficerContact:
              sno.phoneNumber || prev.stateNodalOfficerContact || "",
          }));
        }
        // Populate DNO Details from intervention FK → dnoUser
        if (dprData?.dnoUser) {
          const dno = dprData.dnoUser;
          setFormData((prev) => ({
            ...prev,
            districtNodalOfficerId: dno.id || prev.districtNodalOfficerId || "",
            districtNodalOfficerName:
              [dno.firstName, dno.lastName].filter(Boolean).join(" ") ||
              prev.districtNodalOfficerName ||
              "",
            districtNodalOfficerDesignation:
              dno.division || prev.districtNodalOfficerDesignation || "",
            districtNodalOfficerContact:
              dno.phoneNumber || prev.districtNodalOfficerContact || "",
          }));
        }
      },
      onError: () => {},
      refetchOnMount: true,
    }
  );

  // Fetch project data using projectDetailId resolved from EPID
  const { isLoading: isProjectLoading } = useQuery(
    ["projects", projectId],
    () => getProjectById(projectId),
    {
      enabled: !!projectId,
      onSuccess: (res: AxiosResponse) => {
        setProjectData(res.data);
      },
      onError: (err: AxiosError) => {
        setProjectData(null);
      },
      refetchOnMount: true,
    }
  );

  // Fetch distinct unit values from database
  useQuery("distinctUnits", () => getDistinctUnits(), {
    onSuccess: (res: AxiosResponse) => {
      const units = res.data;
      if (Array.isArray(units)) {
        setUnitOptions(units);
      }
    },
    onError: () => {},
    refetchOnMount: true,
  });

  // Submit DPR mutation
  const { isLoading: isSubmitting, mutate: submitDprMutation } = useMutation(
    "submitDpr",
    (
      payload: ReturnType<typeof transformDprFormToPayload> & {
        remark?: string;
      }
    ) => submitDpr(payload),
    {
      onSuccess: (_data, variables) => {
        const isFinalSubmit = variables?.status === "DPR_SUBMIT";
        setSubmitDialogOpen(false);
        setSubmitRemark("");
        setSnackbar({
          open: true,
          message: isFinalSubmit
            ? "DPR submitted successfully!"
            : "Draft saved successfully!",
          severity: "success",
        });

        // Only a real submit locks the form — a draft save must stay editable.
        if (isFinalSubmit) {
          setHasSubmitted(true);
          // Refetch so dprStatus becomes DPR_SUBMIT, which hides the edit actions.
          queryClient.invalidateQueries(["dpr", landOwnershipEpid]);
          queryClient.invalidateQueries(["dprQueries", landOwnershipEpid]);
        }
      },
      onError: (error: AxiosError<any>) => {
        const errorMessage =
          error.response?.data?.message ||
          "Failed to submit DPR. Please try again.";
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
      },
    }
  );

  // Populate form data with project data
  useEffect(() => {
    if (projectData) {
      const landSpec = projectData.landSpecification || {};
      const address = projectData.address || {};
      const implOfficer = projectData.implementingOfficer || {};
      const snoOfficer = projectData.nodalOfficer || {};

      setFormData((prev) => ({
        ...prev,
        // Part A - General Information
        projectName: projectData.name || "",
        projectType: projectData.projectType || "",
        landParcelId: projectData.registrationNo?.toString() || "",
        contactPerson: prev.contactPerson || implOfficer.firstName || "",
        contactEmail: prev.contactEmail || implOfficer.email || "",
        contactPhone: prev.contactPhone || implOfficer.phoneNumber || "",
        contactPersonDesignation: prev.contactPersonDesignation || "",
        contactPersonAddress:
          prev.contactPersonAddress || address.addressLine1 || "",
        landParcelName: landSpec.siteName || "",
        landArea: prev.landArea || projectData.landArea || 0,
        areaUnit: projectData.areaUnit || "ha",
        gcaId:
          prev.gcaId ||
          projectData.organizationAffiliationDetail?.userDetail?.id ||
          "",
        gcaName:
          prev.gcaName ||
          projectData.organizationAffiliationDetail?.userDetail?.firstName ||
          "",
        gcaType: prev.gcaType || undefined,
        gcaEmail: prev.gcaEmail || "",
        gcaPhone: prev.gcaPhone || "",
        gcaAddress: prev.gcaAddress || "",

        // Location Information
        state: address.stateName || "",
        district: address.districtName || "",
        block: landSpec.revenueBlock || "",
        village: landSpec.soilCharacteristics || "",
        circle: prev.circle || landSpec.circleName || "",
        division: landSpec.division || "",
        range: landSpec.range || "",

        // State and District Nodal Officers
        stateNodalOfficerId: prev.stateNodalOfficerId || snoOfficer.id || "",
        stateNodalOfficerName:
          prev.stateNodalOfficerName || snoOfficer.firstName || "",
        stateNodalOfficerDesignation:
          prev.stateNodalOfficerDesignation || snoOfficer.division || "",
        stateNodalOfficerContact:
          prev.stateNodalOfficerContact || snoOfficer.phoneNumber || "",

        districtNodalOfficerId:
          prev.districtNodalOfficerId || implOfficer.id || "",
        districtNodalOfficerName:
          prev.districtNodalOfficerName || implOfficer.firstName || "",
        districtNodalOfficerDesignation:
          prev.districtNodalOfficerDesignation || implOfficer.division || "",
        districtNodalOfficerContact:
          prev.districtNodalOfficerContact || implOfficer.phoneNumber || "",

        // Part B - Site Characteristics (use prev || landSpec to not overwrite DPR-specific values)
        landUseCategory: prev.landUseCategory || projectData.landType || "",
        landLegalStatus:
          prev.landLegalStatus || landSpec.legalStatusOfLand || "",
        topography: prev.topography || landSpec.terrain || "",
        slopePercentage:
          prev.slopePercentage ??
          (landSpec.slope ? parseFloat(landSpec.slope) : undefined),
        nearestRoadType: prev.nearestRoadType || landSpec.nearestRoadType || "",
        distanceFromRoad: prev.distanceFromRoad || landSpec.accessibility || "",
        distanceFromHabitation:
          prev.distanceFromHabitation ||
          landSpec.proximityToLocalCommunities ||
          "",
        climaticZone: prev.climaticZone || landSpec.climaticZone || "",
        soilType: prev.soilType || landSpec.soilType || "",
        soilDepthTexture:
          prev.soilDepthTexture || landSpec.soilDepthAndTexture || "",
        soilMoistureRegime:
          prev.soilMoistureRegime || landSpec.soilMoistureRegime || "",
        soilPH: prev.soilPH || landSpec.soilpH || "",
        waterSource: prev.waterSource || landSpec.sourceOfWater || "",
        waterSupplyLines:
          prev.waterSupplyLines || landSpec.waterSupplyLines || "",
        distanceToNearestWaterSource:
          prev.distanceToNearestWaterSource ||
          landSpec.distanceToNearestWaterSource ||
          "",
        vegetationType: prev.vegetationType || landSpec.typeOfVegetation || "",
        dominantSpecies: prev.dominantSpecies || landSpec.dominantSpecies || "",
        canopyDensity:
          prev.canopyDensity ??
          (landSpec.treeCanopyDensity
            ? parseFloat(landSpec.treeCanopyDensity)
            : undefined),
        invasiveSpeciesPresent:
          prev.invasiveSpeciesPresent ||
          landSpec.presenceOfInvasiveSpecies ||
          "",
        invasiveSpeciesDetails:
          prev.invasiveSpeciesDetails || landSpec.invasiveSpeciesDetails || "",
        existingFauna: prev.existingFauna || landSpec.existingFauna || "",
        proximityToLocalCommunities:
          prev.proximityToLocalCommunities ||
          landSpec.proximityToLocalCommunities ||
          "",
        nearbySttlements:
          prev.nearbySttlements || landSpec.soilCharacteristics || "",
        rightsAndUsage: prev.rightsAndUsage || landSpec.rightsAndUsage || "",
        communityDependency:
          prev.communityDependency || landSpec.communityDependency || "",
        existingRestrictions:
          prev.existingRestrictions || landSpec.anyExistingRestrictions || "",
      }));

      // Extract KML file URL from documentDetail
      const kmlDoc = projectData.documentDetail?.find(
        (doc: any) => doc.type === "LAND_KML_FILE" && doc.documentUrl
      );
      if (kmlDoc?.documentUrl) {
        setKmlDocumentUrl(kmlDoc.documentUrl);
      }
    }
  }, [projectData]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNextStep = () => {
    if (canEditDpr) {
      const errors = validateStep(activeStep);
      if (errors.length > 0) {
        setAttemptedSteps((prev) => new Set(prev).add(activeStep));
        const displayErrors = errors.slice(0, 5).join(", ");
        const remaining = errors.length - 5;
        setSnackbar({
          open: true,
          message: `Please fill all required fields before proceeding: ${displayErrors}${
            remaining > 0 ? ` and ${remaining} more` : ""
          }`,
          severity: "error",
        });
        return;
      }
    }
    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePrevStep = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const [isKmlUploading, setIsKmlUploading] = useState(false);

  const handleKmlUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setKmlFile(file);
    setIsKmlUploading(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      const res = await fileUpload(formDataUpload as any);
      const uploaded =
        (res as any)?.data?.data?.uploadedFile ||
        (res as any)?.data?.uploadedFile;
      if (uploaded?.key) {
        handleInputChange("kmlFileId", uploaded.key);
        handleInputChange("kmlFileName", file.name);
        if (uploaded.fileUrl) {
          setInterventionKmlUrl(uploaded.fileUrl);
        }
        setSnackbar({
          open: true,
          message: "KML file uploaded successfully!",
          severity: "success",
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to upload KML file. Please try again.",
        severity: "error",
      });
      setKmlFile(null);
    } finally {
      setIsKmlUploading(false);
    }
  };

  // Helper functions for managing year-wise targets
  const addTarget = (yearNumber: number) => {
    const fieldName =
      `yearwiseTargetsData${yearNumber}` as keyof typeof formData;
    const currentTargets = (formData[fieldName] || []) as TargetEntry[];
    const newTarget: TargetEntry = {
      id: `target-${Date.now()}`,
      indicator: "",
      activity: "",
      unit: "",
      numberOfUnits: undefined,
      costPerUnit: undefined,
      costRequired: undefined,
    };
    handleInputChange(fieldName, [...currentTargets, newTarget]);
  };

  const removeTarget = (yearNumber: number, targetId: string | undefined) => {
    const fieldName =
      `yearwiseTargetsData${yearNumber}` as keyof typeof formData;
    const currentTargets = (formData[fieldName] || []) as TargetEntry[];
    const filteredTargets = currentTargets.filter((t) => t.id !== targetId);
    handleInputChange(fieldName, filteredTargets);
  };

  const updateTarget = (
    yearNumber: number,
    targetId: string | undefined,
    field: keyof TargetEntry,
    value: string | number | undefined
  ) => {
    const fieldName =
      `yearwiseTargetsData${yearNumber}` as keyof typeof formData;
    const currentTargets = (formData[fieldName] || []) as TargetEntry[];
    const updatedTargets = currentTargets.map((t) => {
      if (t.id !== targetId) return t;
      const updated = { ...t, [field]: value };
      // Reset activity when indicator changes
      if (field === "indicator") {
        updated.activity = "";
      }
      // Auto-calculate costRequired when numberOfUnits or costPerUnit changes
      if (field === "numberOfUnits" || field === "costPerUnit") {
        const units =
          field === "numberOfUnits" ? (value as number) : updated.numberOfUnits;
        const cost =
          field === "costPerUnit" ? (value as number) : updated.costPerUnit;
        updated.costRequired = units && cost ? units * cost : undefined;
      }
      return updated;
    });
    handleInputChange(fieldName, updatedTargets);
  };

  // Step 0: General Information
  const validateStep0 = (): string[] => {
    const errors: string[] = [];
    if (!formData.landParcelId) errors.push("Land Parcel ID");
    if (!formData.district) errors.push("District");
    if (!formData.circle) errors.push("Circle");
    if (!formData.division) errors.push("Division");
    if (!formData.range) errors.push("Range");
    if (!formData.landArea) errors.push("Land Area");
    if (!formData.acknowledgementDate) errors.push("Acknowledgement Date");
    if (!formData.estimatedGreenCredits) errors.push("Estimated Green Credits");
    if (!formData.greenCreditUsageProjects)
      errors.push("Green Credit Usage Projects");
    return errors;
  };

  // Step 1: Site Characteristics
  const validateStep1 = (): string[] => {
    const errors: string[] = [];
    if (!formData.landUseCategory) errors.push("Land Use Category");
    if (!formData.landLegalStatus) errors.push("Legal Status of Land");
    if (!formData.topography) errors.push("Topography");
    if (
      formData.slopePercentage === undefined ||
      formData.slopePercentage === null
    )
      errors.push("Slope Percentage");
    if (!formData.nearestRoadType) errors.push("Nearest Road");
    if (
      formData.distanceFromHabitation === undefined ||
      formData.distanceFromHabitation === null
    )
      errors.push("Distance from Habitation");
    if (!formData.approachRoute) errors.push("Approach Route");
    if (!formData.climaticZone) errors.push("Climatic Zone");
    if (!formData.inspectionPaths) errors.push("Inspection Paths");
    if (!formData.soilType) errors.push("Soil Type");
    if (!formData.soilDepthTexture) errors.push("Soil Depth and Texture");
    if (!formData.soilMoistureRegime) errors.push("Soil Moisture Regime");
    if (formData.soilPH === undefined || formData.soilPH === null)
      errors.push("Soil pH");
    if (!formData.soilFertilityStatus) errors.push("Soil Fertility Status");
    if (!formData.waterSource) errors.push("Source of Water");
    if (!formData.waterSupplyLines) errors.push("Water Supply Lines");
    if (
      formData.distanceToNearestWaterSource === undefined ||
      formData.distanceToNearestWaterSource === null
    )
      errors.push("Distance to Nearest Water Source");
    if (!formData.suitabilityForConservation)
      errors.push("Suitability for Conservation");
    if (!formData.vegetationType) errors.push("Type of Vegetation");
    if (!formData.dominantSpecies) errors.push("Dominant Species");
    if (formData.canopyDensity === undefined || formData.canopyDensity === null)
      errors.push("Canopy Density");
    if (
      formData.treesGirth0to10 === undefined ||
      formData.treesGirth0to10 === null
    )
      errors.push("Trees Girth 0-10 cm");
    if (
      formData.treesGirth10to30 === undefined ||
      formData.treesGirth10to30 === null
    )
      errors.push("Trees Girth 10-30 cm");
    if (
      formData.treesGirth30to60 === undefined ||
      formData.treesGirth30to60 === null
    )
      errors.push("Trees Girth 30-60 cm");
    if (
      formData.treesGirthGreater60 === undefined ||
      formData.treesGirthGreater60 === null
    )
      errors.push("Trees Girth >60 cm");
    if (!formData.invasiveSpeciesPresent)
      errors.push("Invasive Species Present");
    if (!formData.invasiveSpeciesDetails)
      errors.push("Invasive Species Details");
    if (!formData.existingFauna) errors.push("Existing Fauna");
    if (
      formData.proximityToLocalCommunities === undefined ||
      formData.proximityToLocalCommunities === null
    )
      errors.push("Proximity to Local Communities");
    if (!formData.nearbySttlements) errors.push("Nearby Settlements");
    if (!formData.rightsAndUsage) errors.push("Rights & Usage");
    if (!formData.communityDependency) errors.push("Community Dependency");
    if (!formData.existingRestrictions) errors.push("Existing Restrictions");
    return errors;
  };

  // Step 2: Interventions & Zoning
  const validateStep2 = (): string[] => {
    const errors: string[] = [];
    if (!formData.treePlantationArea) errors.push("Tree Plantation Area");
    if (!formData.treePlantationSpecies) errors.push("Tree Plantation Species");
    if (!formData.seedlingsPerHectare) errors.push("Seedlings per Hectare");
    if (!formData.spacingPattern) errors.push("Spacing Pattern");
    if (!formData.nurserySource) errors.push("Nursery Source");
    if (!formData.nurseryLocation) errors.push("Nursery Location");
    if (!formData.temporaryNurseryDetails)
      errors.push("Temporary Nursery Details");
    if (!formData.maintenancePlan) errors.push("Maintenance Plan");
    if (!formData.soilConservationArea) errors.push("Soil Conservation Area");
    if (!formData.conservationWorkType) errors.push("Conservation Work Type");
    if (!formData.conservationWorkZone) errors.push("Conservation Work Zone");
    if (!formData.conservationWorkPurpose)
      errors.push("Conservation Work Purpose");
    if (!formData.waterSupplyArea) errors.push("Water Supply Area");
    if (!formData.proposedWaterSource) errors.push("Proposed Water Source");
    if (!formData.supplyLineLayout) errors.push("Supply Line Layout");
    if (!formData.waterHarvestingStructures)
      errors.push("Water Harvesting Structures");
    if (!formData.protectionArea) errors.push("Protection Area");
    if (!formData.fencingType) errors.push("Fencing Type");
    if (!formData.fencingLength) errors.push("Fencing Length");
    if (!formData.firePreventionWorks) errors.push("Fire Prevention Works");
    if (!formData.watchWardArrangements)
      errors.push("Watch & Ward Arrangements");
    if (!formData.biodiversityArea) errors.push("Biodiversity Area");
    if (!formData.pollinatorSpecies) errors.push("Pollinator Species");
    if (!formData.ntfpSpecies) errors.push("NTFP Species");
    if (!formData.grassesShrubsMedicinal)
      errors.push("Grasses, Shrubs & Medicinal Plants");
    return errors;
  };

  // Step 3: Financial Outlay — no required fields to block (costPerUnit is per-target, validated on submit)

  // Step 4: Declarations
  const validateStep4 = (): string[] => {
    const errors: string[] = [];
    if (!formData.declarationTrueComplete)
      errors.push("Declaration: Truthfulness");
    if (!formData.declarationAbideRules) errors.push("Declaration: Compliance");
    if (!formData.declarationAdequateResources)
      errors.push("Declaration: Resources");
    if (!formData.declarationMaintenanceFund)
      errors.push("Declaration: Maintenance Fund");
    if (!formData.declarationPerformanceParameters)
      errors.push("Declaration: Performance");
    if (!formData.declarationProgressReports)
      errors.push("Declaration: Progress Reports");
    if (!formData.declarationNoLandClaim)
      errors.push("Declaration: No Land Claim");
    if (!formData.declarationNoCommercialActivity)
      errors.push("Declaration: No Commercial Activity");
    return errors;
  };

  const validateStep = (step: number): string[] => {
    switch (step) {
      case 0:
        return validateStep0();
      case 1:
        return validateStep1();
      case 2:
        return validateStep2();
      case 3:
        return []; // Financial Outlay — no blocking fields
      case 4:
        return validateStep4();
      default:
        return [];
    }
  };

  const validateRequiredFields = (): string[] => {
    return [
      ...validateStep0(),
      ...validateStep1(),
      ...validateStep2(),
      ...validateStep4(),
    ];
  };

  const handleSubmit = (remark?: string) => {
    if (!landOwnershipEpid) {
      setSnackbar({
        open: true,
        message: "Land Ownership EPID is missing.",
        severity: "error",
      });
      return;
    }
    if (!projectId) {
      setSnackbar({
        open: true,
        message: "Land Ownership ID is missing.",
        severity: "error",
      });
      return;
    }

    const validationErrors = validateRequiredFields();
    if (validationErrors.length > 0) {
      setAttemptedSteps(new Set([0, 1, 2, 3, 4]));
      const displayErrors = validationErrors.slice(0, 5).join(", ");
      const remaining = validationErrors.length - 5;
      setSnackbar({
        open: true,
        message: `Please fill all required fields: ${displayErrors}${
          remaining > 0 ? ` and ${remaining} more` : ""
        }`,
        severity: "error",
      });
      return;
    }

    const payload = transformDprFormToPayload(
      formData as DPRFormData,
      projectId,
      landOwnershipEpid,
      "DPR_SUBMIT"
    );
    submitDprMutation({ ...payload, remark: remark?.trim() || undefined });
  };

  const handleSaveAsDraft = () => {
    if (!landOwnershipEpid || !projectId) {
      setSnackbar({
        open: true,
        message: "Land Ownership EPID is missing. Cannot save.",
        severity: "error",
      });
      return;
    }
    const payload = transformDprFormToPayload(
      formData as DPRFormData,
      projectId,
      landOwnershipEpid,
      "DPR_DRAFT"
    );
    submitDprMutation(payload);
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const steps = [
    "General Information",
    "Site Characteristics",
    "Interventions & Zoning",
    "Financial Outlay",
    "Declarations and Undertakings",
  ];

  const isDataLoading = isDprLoading || isProjectLoading;

  return (
    <Container maxWidth="lg" sx={styles.container}>
      {isDataLoading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            gap: 2,
          }}
        >
          <CircularProgress size={48} sx={{ color: "#36B1A0" }} />
          <Typography
            variant="body1"
            sx={{ color: "#6B7280", fontWeight: 500 }}
          >
            Loading DPR data...
          </Typography>
        </Box>
      ) : (
        <>
          {/* Header */}
          <Box
            sx={{
              mb: 4,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: "#1F2937", mb: 1 }}
              >
                Digital Project Report (DPR)
                {dprStatus && (
                  <Chip
                    label={dprStatus.replace(/_/g, " ")}
                    size="small"
                    sx={{
                      ml: 2,
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      verticalAlign: "middle",
                      bgcolor:
                        dprStatus === "DPR_APPROVED"
                          ? "#DEF7EC"
                          : dprStatus === "DPR_REJECTED"
                          ? "#FDE8E8"
                          : dprStatus.includes("QUERY")
                          ? "#FEF3C7"
                          : "#DBEAFE",
                      color:
                        dprStatus === "DPR_APPROVED"
                          ? "#03543F"
                          : dprStatus === "DPR_REJECTED"
                          ? "#9B1C1C"
                          : dprStatus.includes("QUERY")
                          ? "#92400E"
                          : "#1E40AF",
                    }}
                  />
                )}
              </Typography>
              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                Complete all sections to submit your project details
              </Typography>
              {dprVersionId && (
                <Typography
                  variant="body2"
                  sx={{ color: "#6B7280", fontWeight: 500 }}
                >
                  DPR Version: {dprVersionId}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                flexShrink: 0,
              }}
            >
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => downloadPdf(DPR_FORMAT_PDF, "DPR-Format.pdf")}
                sx={{
                  ...styles.button,
                  ...styles.primaryButton,
                  whiteSpace: "nowrap",
                }}
              >
                Download DPR Format
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => downloadPdf(MODEL_DPR_PDF, "Modal-DPR.pdf")}
                sx={{
                  ...styles.button,
                  ...styles.secondaryButton,
                  whiteSpace: "nowrap",
                }}
              >
                Model DPR (For Reference Only)
              </Button>
            </Box>
          </Box>

          {/* Stepper */}
          <Card sx={styles.stepper}>
            <Stepper activeStep={activeStep}>
              {steps.map((label, index) => (
                <Step
                  key={label}
                  onClick={() => {
                    if (canEditDpr && index > activeStep) {
                      // Validate all steps up to the current one before allowing forward jump
                      for (let s = activeStep; s < index; s++) {
                        const errors = validateStep(s);
                        if (errors.length > 0) {
                          setAttemptedSteps((prev) => new Set(prev).add(s));
                          const displayErrors = errors.slice(0, 5).join(", ");
                          const remaining = errors.length - 5;
                          setSnackbar({
                            open: true,
                            message: `Please fill all required fields in "${
                              steps[s]
                            }" before proceeding: ${displayErrors}${
                              remaining > 0 ? ` and ${remaining} more` : ""
                            }`,
                            severity: "error",
                          });
                          return;
                        }
                      }
                    }
                    setActiveStep(index);
                  }}
                  sx={{ cursor: "pointer" }}
                >
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Card>

          {/* Executive Summary */}
          {activeStep === 0 && (
            <fieldset
              disabled={!canEditDpr}
              style={{ border: "none", padding: 0, margin: 0 }}
            >
              <Card sx={styles.card}>
                <Box sx={styles.sectionHeader}>
                  <Typography sx={styles.sectionTitle}>
                    GENERAL INFORMATION
                  </Typography>
                </Box>
                <Box sx={styles.sectionContent}>
                  {/* 1. Land Parcel Details */}
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, mb: 2, color: "#1F2937" }}
                  >
                    Land Parcel Details
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Unique Land Parcel ID *
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={attemptedSteps.has(0) && !formData.landParcelId}
                        placeholder="Auto-populated"
                        value={formData.landParcelId || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Name of Land Parcel
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Auto-populated"
                        value={formData.landParcelName || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={styles.fieldLabel}>District *</Typography>
                      <TextField
                        fullWidth
                        required
                        error={attemptedSteps.has(0) && !formData.district}
                        placeholder="Auto-populated"
                        value={formData.district || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={styles.fieldLabel}>Block</Typography>
                      <TextField
                        fullWidth
                        placeholder="Auto-populated"
                        value={formData.block || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={styles.fieldLabel}>Village</Typography>
                      <TextField
                        fullWidth
                        placeholder="Auto-populated"
                        value={formData.village || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={styles.fieldLabel}>Circle</Typography>
                      <TextField
                        fullWidth
                        required
                        error={attemptedSteps.has(0) && !formData.circle}
                        placeholder="Auto-populated"
                        value={formData.circle || ""}
                        onChange={(e) =>
                          handleInputChange("circle", e.target.value)
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={styles.fieldLabel}>Division</Typography>
                      <TextField
                        fullWidth
                        required
                        error={attemptedSteps.has(0) && !formData.division}
                        placeholder="Auto-populated"
                        value={formData.division || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={styles.fieldLabel}>Range</Typography>
                      <TextField
                        fullWidth
                        required
                        error={attemptedSteps.has(0) && !formData.range}
                        placeholder="Auto-populated"
                        value={formData.range || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography sx={styles.fieldLabel}>
                        Shapefiles (KML/KMZ format)
                      </Typography>
                      {/* Show existing KML from project data */}
                      {kmlDocumentUrl && (
                        <Box sx={{ mb: 2 }}>
                          <Box
                            component="a"
                            href={kmlDocumentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: "#36B1A0",
                              fontWeight: 500,
                              textDecoration: "underline",
                              cursor: "pointer",
                              fontSize: "0.9rem",
                            }}
                          >
                            View / Download KML File
                          </Box>
                          <Box
                            sx={{
                              border: "1px solid #E0E0E0",
                              borderRadius: "8px",
                              overflow: "hidden",
                              height: 400,
                            }}
                          >
                            <MapComponent kmlUrl={kmlDocumentUrl} />
                          </Box>
                        </Box>
                      )}
                      {/* Upload new shapefile */}
                      <Box sx={styles.dropzone}>
                        <CloudUploadIcon
                          sx={{ fontSize: "2rem", color: "#36B1A0", mb: 1 }}
                        />
                        <Typography
                          sx={{ fontWeight: 600, color: "#1F2937", mb: 1 }}
                        >
                          Upload Shapefiles
                        </Typography>
                        <input
                          accept=".kml,.kmz,.shp"
                          type="file"
                          onChange={handleKmlUpload}
                          style={{ display: "none" }}
                          id="shapefile-upload"
                        />
                        <label htmlFor="shapefile-upload">
                          <Button
                            variant="contained"
                            component="span"
                            sx={{ mt: 1 }}
                          >
                            Browse Files
                          </Button>
                        </label>
                        {isKmlUploading && (
                          <Box
                            sx={{
                              mt: 2,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <CircularProgress size={18} />
                            <Typography sx={{ color: "#6B7280" }}>
                              Uploading...
                            </Typography>
                          </Box>
                        )}
                        {!isKmlUploading &&
                          (kmlFile || formData.kmlFileName) && (
                            <Typography
                              sx={{ mt: 2, color: "#073706", fontWeight: 600 }}
                            >
                              ✓ File: {kmlFile?.name || formData.kmlFileName}
                            </Typography>
                          )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Area of Land Parcel *
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={attemptedSteps.has(0) && !formData.landArea}
                        type="number"
                        placeholder="0.00"
                        value={formData.landArea || ""}
                        onChange={(e) =>
                          handleInputChange("landArea", Number(e.target.value))
                        }
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              hectares
                            </InputAdornment>
                          ),
                        }}
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Date of Issue of Acknowledgement *
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(0) && !formData.acknowledgementDate
                        }
                        type="date"
                        value={formData.acknowledgementDate || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "acknowledgementDate",
                            e.target.value
                          )
                        }
                        sx={styles.inputField}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>

                  {/* 2. GCA Details */}
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, mb: 2, color: "#1F2937" }}
                  >
                    Green Credit Applicant (GCA) Details
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Name of GCA *
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Auto-populated from registration"
                        value={formData.gcaName || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Legal Status *
                      </Typography>
                      <TextField
                        fullWidth
                        value={formData.gcaType || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Registration/Incorporation Number (PAN/CIN/GSTN) *
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="e.g., PAN/CIN/GSTN"
                        value={formData.registrationNumber || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        GCA Address *
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Complete address"
                        value={formData.gcaAddress || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        GCA Email *
                      </Typography>
                      <TextField
                        fullWidth
                        type="email"
                        placeholder="email@example.com"
                        value={formData.gcaEmail || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        GCA Phone *
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="+91 XXXXX XXXXX"
                        value={formData.gcaPhone || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Contact Person/Authorised Representative (Name) *
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Full name"
                        value={formData.contactPerson || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Designation *
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="e.g., CEO, Manager"
                        value={formData.contactPersonDesignation || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Contact Person Address *
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Complete address"
                        value={formData.contactPersonAddress || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Contact Person Email *
                      </Typography>
                      <TextField
                        fullWidth
                        type="email"
                        placeholder="email@example.com"
                        value={formData.contactEmail || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Contact Person Mobile Number * (OTP verified)
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="+91 XXXXX XXXXX"
                        value={formData.contactPhone || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                  </Grid>

                  {/* 3. Nodal Officer Details */}
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, mb: 2, color: "#1F2937" }}
                  >
                    Nodal Officer Details
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12}>
                      <Typography
                        sx={{ fontWeight: 600, color: "#1F2937", mb: 2 }}
                      >
                        State Nodal Officer
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={styles.fieldLabel}>Name *</Typography>
                      <TextField
                        fullWidth
                        placeholder="Auto-populated"
                        value={formData.stateNodalOfficerName || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={styles.fieldLabel}>
                        Designation *
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Auto-populated"
                        value={formData.stateNodalOfficerDesignation || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={styles.fieldLabel}>Contact *</Typography>
                      <TextField
                        fullWidth
                        placeholder="Auto-populated"
                        value={formData.stateNodalOfficerContact || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        sx={{ fontWeight: 600, color: "#1F2937", mb: 2 }}
                      >
                        District Nodal Officer
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={styles.fieldLabel}>Name *</Typography>
                      <TextField
                        fullWidth
                        placeholder="Auto-populated"
                        value={formData.districtNodalOfficerName || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={styles.fieldLabel}>
                        Designation *
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Auto-populated"
                        value={formData.districtNodalOfficerDesignation || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={styles.fieldLabel}>Contact *</Typography>
                      <TextField
                        fullWidth
                        placeholder="Auto-populated"
                        value={formData.districtNodalOfficerContact || ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          ...styles.inputField,
                          backgroundColor: "#F3F4F6",
                        }}
                      />
                    </Grid>
                  </Grid>

                  {/* 4. Purpose of DPR */}
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, mb: 2, color: "#1F2937" }}
                  >
                    Purpose of DPR under Green Credit Programme (Multiple
                    choice)
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={(formData.dprPurpose || []).includes(
                                DPRPurpose.COMPENSATORY_AFFORESTATION
                              )}
                              onChange={(e) => {
                                const current = formData.dprPurpose || [];
                                if (e.target.checked) {
                                  handleInputChange("dprPurpose", [
                                    ...current,
                                    DPRPurpose.COMPENSATORY_AFFORESTATION,
                                  ]);
                                } else {
                                  handleInputChange(
                                    "dprPurpose",
                                    current.filter(
                                      (item) =>
                                        item !==
                                        DPRPurpose.COMPENSATORY_AFFORESTATION
                                    )
                                  );
                                }
                              }}
                            />
                          }
                          label="Compensatory Afforestation compliance"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={(formData.dprPurpose || []).includes(
                                DPRPurpose.CSR
                              )}
                              onChange={(e) => {
                                const current = formData.dprPurpose || [];
                                if (e.target.checked) {
                                  handleInputChange("dprPurpose", [
                                    ...current,
                                    DPRPurpose.CSR,
                                  ]);
                                } else {
                                  handleInputChange(
                                    "dprPurpose",
                                    current.filter(
                                      (item) => item !== DPRPurpose.CSR
                                    )
                                  );
                                }
                              }}
                            />
                          }
                          label="Corporate Social Responsibility compliance"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={(formData.dprPurpose || []).includes(
                                DPRPurpose.ESG
                              )}
                              onChange={(e) => {
                                const current = formData.dprPurpose || [];
                                if (e.target.checked) {
                                  handleInputChange("dprPurpose", [
                                    ...current,
                                    DPRPurpose.ESG,
                                  ]);
                                } else {
                                  handleInputChange(
                                    "dprPurpose",
                                    current.filter(
                                      (item) => item !== DPRPurpose.ESG
                                    )
                                  );
                                }
                              }}
                            />
                          }
                          label="Environment, Social and Governance (ESG) leadership framework"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={(formData.dprPurpose || []).includes(
                                DPRPurpose.TREE_PLANTATION_COMPLIANCE
                              )}
                              onChange={(e) => {
                                const current = formData.dprPurpose || [];
                                if (e.target.checked) {
                                  handleInputChange("dprPurpose", [
                                    ...current,
                                    DPRPurpose.TREE_PLANTATION_COMPLIANCE,
                                  ]);
                                } else {
                                  handleInputChange(
                                    "dprPurpose",
                                    current.filter(
                                      (item) =>
                                        item !==
                                        DPRPurpose.TREE_PLANTATION_COMPLIANCE
                                    )
                                  );
                                }
                              }}
                            />
                          }
                          label="Tree Plantation Compliance under statutory requirements"
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  {/* 5. Utilisation Plan for Green Credits */}
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, mb: 2, color: "#1F2937" }}
                  >
                    Utilisation Plan for Green Credits
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Number of GCs Expected to be Generated *
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(0) &&
                          !formData.estimatedGreenCredits
                        }
                        type="number"
                        placeholder="0"
                        value={formData.estimatedGreenCredits || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "estimatedGreenCredits",
                            Number(e.target.value)
                          )
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography sx={styles.fieldLabel}>
                        Projects/Activities for GC Utilisation
                      </Typography>
                      <TextareaAutosize
                        minRows={4}
                        required
                        placeholder="Describe projects or activities where GC will be used (if for tree plantation and compensatory afforestation compliance)"
                        style={{
                          width: "100%",
                          padding: "12px",
                          borderRadius: "8px",
                          borderColor:
                            attemptedSteps.has(0) &&
                            !formData.greenCreditUsageProjects
                              ? "#d32f2f"
                              : "#E5E7EB",
                          borderWidth:
                            attemptedSteps.has(0) &&
                            !formData.greenCreditUsageProjects
                              ? "2px"
                              : "1px",
                          fontFamily: "inherit",
                          fontSize: "14px",
                        }}
                        value={formData.greenCreditUsageProjects || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "greenCreditUsageProjects",
                            e.target.value
                          )
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Card>
            </fieldset>
          )}

          {/* Part B - Site Characteristics */}
          {activeStep === 1 && (
            <fieldset
              disabled={!canEditDpr}
              style={{ border: "none", padding: 0, margin: 0 }}
            >
              <Card sx={styles.card}>
                <Box sx={styles.sectionHeader}>
                  <Typography sx={styles.sectionTitle}>
                    Site Baseline & Characteristics
                  </Typography>
                </Box>
                <Box sx={styles.sectionContent}>
                  {/* 1. General Site Description */}
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, mb: 2, color: "#1F2937" }}
                  >
                    General Site Description
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Land Use Category *
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) && !formData.landUseCategory
                        }
                        placeholder="e.g., Agricultural, Forest, Barren"
                        value={formData.landUseCategory || ""}
                        onChange={(e) =>
                          handleInputChange("landUseCategory", e.target.value)
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Legal Status of Land *
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) && !formData.landLegalStatus
                        }
                        placeholder="e.g., Government, Private, Community"
                        value={formData.landLegalStatus || ""}
                        onChange={(e) =>
                          handleInputChange("landLegalStatus", e.target.value)
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Topography *
                      </Typography>
                      <FormControl
                        fullWidth
                        sx={styles.inputField}
                        error={attemptedSteps.has(1) && !formData.topography}
                      >
                        <Select
                          required
                          value={formData.topography || ""}
                          onChange={(e) =>
                            handleInputChange("topography", e.target.value)
                          }
                        >
                          <MenuItem value="">Select...</MenuItem>
                          <MenuItem value="PLAIN">Plain</MenuItem>
                          <MenuItem value="UNDULATING">Undulating</MenuItem>
                          <MenuItem value="HILLY">Hilly</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Slope Percentage
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) &&
                          (formData.slopePercentage === undefined ||
                            formData.slopePercentage === null)
                        }
                        type="number"
                        placeholder="0-100"
                        value={formData.slopePercentage || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "slopePercentage",
                            Number(e.target.value)
                          )
                        }
                        sx={styles.inputField}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Nearest Road *
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) && !formData.nearestRoadType
                        }
                        placeholder="e.g., National Highway, State Road, Village Road"
                        value={formData.nearestRoadType || ""}
                        onChange={(e) =>
                          handleInputChange("nearestRoadType", e.target.value)
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                    {/* <Grid item xs={12} sm={6}>
                <Typography sx={styles.fieldLabel}>Distance from Road (km)</Typography>
                <TextField
                  fullWidth
                  type="number"
                  placeholder="0.0"
                  value={formData.distanceFromRoad || ''}
                  onChange={(e) => handleInputChange('distanceFromRoad', Number(e.target.value))}
                  sx={styles.inputField}
                /> */}
                    {/* </Grid> */}
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Distance from Habitation (km)
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) &&
                          (formData.distanceFromHabitation === undefined ||
                            formData.distanceFromHabitation === null)
                        }
                        type="number"
                        placeholder="0.0"
                        value={formData.distanceFromHabitation || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "distanceFromHabitation",
                            Number(e.target.value)
                          )
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Approach Route Description
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={attemptedSteps.has(1) && !formData.approachRoute}
                        placeholder="Describe the accessibility and approach routes"
                        value={formData.approachRoute || ""}
                        onChange={(e) =>
                          handleInputChange("approachRoute", e.target.value)
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Climatic Zone *
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={attemptedSteps.has(1) && !formData.climaticZone}
                        placeholder="Auto-suggested based on location"
                        value={formData.climaticZone || ""}
                        onChange={(e) =>
                          handleInputChange("climaticZone", e.target.value)
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Available Inspection Paths
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) && !formData.inspectionPaths
                        }
                        placeholder="May be auto-suggested based on DNO inputs"
                        value={formData.inspectionPaths || ""}
                        onChange={(e) =>
                          handleInputChange("inspectionPaths", e.target.value)
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                  </Grid>

                  {/* 2. Soil Characteristics */}
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, mb: 2, color: "#1F2937" }}
                  >
                    Soil Characteristics
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Soil Type *
                      </Typography>
                      <FormControl fullWidth sx={styles.inputField}>
                        <TextField
                          fullWidth
                          required
                          error={attemptedSteps.has(1) && !formData.soilType}
                          placeholder="Soil Type (e.g., Sandy, Clayey, Loamy, Lateritic, etc.)"
                          value={formData.soilType || ""}
                          onChange={(e) =>
                            handleInputChange("soilType", e.target.value)
                          }
                          sx={styles.inputField}
                        />
                        {/* <Select
                    value={formData.soilType || ""}
                    onChange={(e) =>
                      handleInputChange("soilType", e.target.value as any)
                    }
                  >
                    <MenuItem value="">Select...</MenuItem>
                    <MenuItem value="SANDY">Sandy</MenuItem>
                    <MenuItem value="CLAYEY">Clayey</MenuItem>
                    <MenuItem value="LOAMY">Loamy</MenuItem>
                    <MenuItem value="LATERITIC">Lateritic</MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                  </Select> */}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Soil Depth and Texture
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) && !formData.soilDepthTexture
                        }
                        placeholder="Field observation / auto-suggested from soil maps"
                        value={formData.soilDepthTexture || ""}
                        onChange={(e) =>
                          handleInputChange("soilDepthTexture", e.target.value)
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Soil Moisture Regime *
                      </Typography>
                      <FormControl fullWidth sx={styles.inputField}>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(1) &&
                            !formData.soilMoistureRegime
                          }
                          placeholder="Soil Moisture Regime (e.g., Dry, Moist, Seasonal Waterlogged, etc.)"
                          value={formData.soilMoistureRegime || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "soilMoistureRegime",
                              e.target.value
                            )
                          }
                          sx={styles.inputField}
                        />
                        {/* <Select
                    value={formData.soilMoistureRegime || ""}
                    onChange={(e) =>
                      handleInputChange("soilMoistureRegime", e.target.value)
                    }
                  >
                    <MenuItem value="">Select...</MenuItem>
                    <MenuItem value="DRY">Dry</MenuItem>
                    <MenuItem value="MOIST">Moist</MenuItem>
                    <MenuItem value="SEASONAL_WATERLOGGED">
                      Seasonal Waterlogged
                    </MenuItem>
                  </Select> */}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>Soil pH</Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) &&
                          (formData.soilPH === undefined ||
                            formData.soilPH === null)
                        }
                        type="number"
                        placeholder="0-14"
                        value={formData.soilPH || ""}
                        onChange={(e) =>
                          handleInputChange("soilPH", Number(e.target.value))
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography sx={styles.fieldLabel}>
                        Soil Fertility Status
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) && !formData.soilFertilityStatus
                        }
                        placeholder="e.g., High, Medium, Low / Rich, Moderate, Poor"
                        value={formData.soilFertilityStatus || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "soilFertilityStatus",
                            e.target.value
                          )
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                  </Grid>

                  {/* 3. Water Availability */}
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, mb: 2, color: "#1F2937" }}
                  >
                    Water Availability
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Source of Water *
                      </Typography>
                      <FormControl fullWidth sx={styles.inputField}>
                        <TextField
                          fullWidth
                          required
                          error={attemptedSteps.has(1) && !formData.waterSource}
                          placeholder="Water Source (e.g., Natural Streams, Hand Pumps, Bore Wells, Tanks, etc.)"
                          value={formData.waterSource || ""}
                          onChange={(e) =>
                            handleInputChange("waterSource", e.target.value)
                          }
                          sx={styles.inputField}
                        />
                        {/* <Select
                    value={formData.waterSource || ""}
                    onChange={(e) =>
                      handleInputChange("waterSource", e.target.value as any)
                    }
                  >
                    <MenuItem value="">Select...</MenuItem>
                    <MenuItem value="NATURAL_STREAMS">Natural Streams</MenuItem>
                    <MenuItem value="HAND_PUMPS">Hand Pumps</MenuItem>
                    <MenuItem value="BORE_WELLS">Bore Wells</MenuItem>
                    <MenuItem value="TANKS">Tanks</MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                  </Select> */}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Water Supply Lines in Land Parcel
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) && !formData.waterSupplyLines
                        }
                        placeholder="If available, describe existing water supply lines"
                        value={formData.waterSupplyLines || ""}
                        onChange={(e) =>
                          handleInputChange("waterSupplyLines", e.target.value)
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Distance to Nearest Water Source (km)
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) &&
                          (formData.distanceToNearestWaterSource ===
                            undefined ||
                            formData.distanceToNearestWaterSource === null)
                        }
                        type="number"
                        placeholder="0.0 (auto-suggested based on DNO inputs)"
                        value={formData.distanceToNearestWaterSource || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "distanceToNearestWaterSource",
                            Number(e.target.value)
                          )
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Suitable for Soil & Moisture Conservation Works
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) &&
                          !formData.suitabilityForConservation
                        }
                        placeholder="Yes/No/Partial"
                        value={formData.suitabilityForConservation || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "suitabilityForConservation",
                            e.target.value
                          )
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                  </Grid>

                  {/* 4. Existing Vegetation */}
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, mb: 2, color: "#1F2937" }}
                  >
                    Existing Vegetation
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Type of Vegetation *
                      </Typography>
                      <FormControl fullWidth sx={styles.inputField}>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(1) && !formData.vegetationType
                          }
                          placeholder="Auto-suggested based on location"
                          value={formData.vegetationType || ""}
                          onChange={(e) =>
                            handleInputChange("vegetationType", e.target.value)
                          }
                          sx={styles.inputField}
                        />
                        {/* <Select
                    value={formData.vegetationType || ""}
                    onChange={(e) =>
                      handleInputChange("vegetationType", e.target.value as any)
                    }
                  >
                    <MenuItem value="">Select...</MenuItem>
                    <MenuItem value="SCRUB">Scrub</MenuItem>
                    <MenuItem value="GRASSLAND">Grassland</MenuItem>
                    <MenuItem value="REGENERATING_FOREST">
                      Regenerating Forest
                    </MenuItem>
                    <MenuItem value="DEGRADED_PLANTATION">
                      Degraded Plantation
                    </MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                  </Select> */}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Dominant Species (Local/Native) *
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) && !formData.dominantSpecies
                        }
                        placeholder="Enter local/native species names"
                        value={formData.dominantSpecies || ""}
                        onChange={(e) =>
                          handleInputChange("dominantSpecies", e.target.value)
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Canopy Density (%) - Baseline Assessment
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) &&
                          (formData.canopyDensity === undefined ||
                            formData.canopyDensity === null)
                        }
                        type="number"
                        placeholder="Based on Drone survey (0-100)"
                        value={formData.canopyDensity || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "canopyDensity",
                            Number(e.target.value)
                          )
                        }
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Number of Trees by Girth Class (0-10 cm)
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) &&
                          (formData.treesGirth0to10 === undefined ||
                            formData.treesGirth0to10 === null)
                        }
                        type="number"
                        placeholder="Count from baseline enumeration"
                        value={formData.treesGirth0to10 || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "treesGirth0to10",
                            Number(e.target.value)
                          )
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Number of Trees by Girth Class (10-30 cm)
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) &&
                          (formData.treesGirth10to30 === undefined ||
                            formData.treesGirth10to30 === null)
                        }
                        type="number"
                        placeholder="Count from baseline enumeration"
                        value={formData.treesGirth10to30 || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "treesGirth10to30",
                            Number(e.target.value)
                          )
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Number of Trees by Girth Class (30-60 cm)
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) &&
                          (formData.treesGirth30to60 === undefined ||
                            formData.treesGirth30to60 === null)
                        }
                        type="number"
                        placeholder="Count from baseline enumeration"
                        value={formData.treesGirth30to60 || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "treesGirth30to60",
                            Number(e.target.value)
                          )
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Number of Trees by Girth Class (above 60 cm)
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) &&
                          (formData.treesGirthGreater60 === undefined ||
                            formData.treesGirthGreater60 === null)
                        }
                        type="number"
                        placeholder="Count from baseline enumeration"
                        value={formData.treesGirthGreater60 || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "treesGirthGreater60",
                            Number(e.target.value)
                          )
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Presence of Invasive Species
                      </Typography>
                      <FormControl
                        fullWidth
                        sx={styles.inputField}
                        error={
                          attemptedSteps.has(1) &&
                          !formData.invasiveSpeciesPresent
                        }
                      >
                        <Select
                          required
                          value={formData.invasiveSpeciesPresent || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "invasiveSpeciesPresent",
                              e.target.value
                            )
                          }
                        >
                          <MenuItem value="">Select...</MenuItem>
                          <MenuItem value="YES">Yes</MenuItem>
                          <MenuItem value="NO">No</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography sx={styles.fieldLabel}>
                        Invasive Species Details (if applicable)
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) &&
                          !formData.invasiveSpeciesDetails
                        }
                        placeholder="Describe invasive species present, if any"
                        value={formData.invasiveSpeciesDetails || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "invasiveSpeciesDetails",
                            e.target.value
                          )
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                  </Grid>

                  {/* 5. Existing Fauna */}
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, mb: 2, color: "#1F2937" }}
                  >
                    Existing Fauna
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        error={attemptedSteps.has(1) && !formData.existingFauna}
                        placeholder="Describe existing fauna, wildlife, birds, insects, etc."
                        value={formData.existingFauna || ""}
                        onChange={(e) =>
                          handleInputChange("existingFauna", e.target.value)
                        }
                        multiline
                        sx={styles.inputField}
                      />
                    </Grid>
                  </Grid>

                  {/* 6. Socio-Environmental Context */}
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, mb: 2, color: "#1F2937" }}
                  >
                    Socio-Environmental Context
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Proximity to Local Communities (km)
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) &&
                          (formData.proximityToLocalCommunities === undefined ||
                            formData.proximityToLocalCommunities === null)
                        }
                        type="number"
                        placeholder="Distance to nearest settlements"
                        value={formData.proximityToLocalCommunities || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "proximityToLocalCommunities",
                            Number(e.target.value)
                          )
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Nearby Settlements Description
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) && !formData.nearbySttlements
                        }
                        placeholder="Describe nearby settlements, villages, towns"
                        value={formData.nearbySttlements || ""}
                        onChange={(e) =>
                          handleInputChange("nearbySttlements", e.target.value)
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Rights & Usage (FRA or Customary Use)
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) && !formData.rightsAndUsage
                        }
                        placeholder="If any rights are vested under FRA or customary use"
                        value={formData.rightsAndUsage || ""}
                        onChange={(e) =>
                          handleInputChange("rightsAndUsage", e.target.value)
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.fieldLabel}>
                        Community Dependency
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) && !formData.communityDependency
                        }
                        placeholder="e.g., Fuelwood, Grazing, NTFP, etc."
                        value={formData.communityDependency || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "communityDependency",
                            e.target.value
                          )
                        }
                        sx={styles.inputField}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography sx={styles.fieldLabel}>
                        Existing Restrictions (Legal or Customary)
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        error={
                          attemptedSteps.has(1) &&
                          !formData.existingRestrictions
                        }
                        placeholder="Describe any legal or customary restrictions"
                        value={formData.existingRestrictions || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "existingRestrictions",
                            e.target.value
                          )
                        }
                        multiline
                        sx={styles.inputField}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Card>
            </fieldset>
          )}

          {/* Part C - Interventions & Zoning */}
          {activeStep === 2 && (
            <fieldset
              disabled={!canEditDpr}
              style={{ border: "none", padding: 0, margin: 0 }}
            >
              <Card sx={styles.card}>
                <Box sx={styles.sectionHeader}>
                  <Typography sx={styles.sectionTitle}>
                    Proposed Interventions & Zoning of Activities
                  </Typography>
                </Box>
                <Box sx={styles.sectionContent}>
                  {/* 1. Zoning of Activities */}
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, mb: 2, color: "#1F2937" }}
                  >
                    Zoning of Activities (KML File Upload)
                  </Typography>
                  <Box
                    sx={styles.dropzone}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (e.dataTransfer.files?.[0]) {
                        const fakeEvent = {
                          target: { files: e.dataTransfer.files },
                        } as React.ChangeEvent<HTMLInputElement>;
                        handleKmlUpload(fakeEvent);
                      }
                    }}
                  >
                    <CloudUploadIcon
                      sx={{ fontSize: "3rem", color: "#36B1A0", mb: 1 }}
                    />
                    <Typography
                      sx={{ fontWeight: 600, color: "#1F2937", mb: 1 }}
                    >
                      Upload KML/KMZ File
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#6B7280", mb: 2 }}
                    >
                      Drag and drop your KML file depicting zones for different
                      interventions
                    </Typography>
                    <input
                      accept=".kml,.kmz"
                      type="file"
                      onChange={handleKmlUpload}
                      style={{ display: "none" }}
                      id="kml-upload"
                    />
                    <label htmlFor="kml-upload">
                      <Button
                        variant="contained"
                        component="span"
                        sx={{ mt: 1 }}
                      >
                        Browse Files
                      </Button>
                    </label>
                    {isKmlUploading && (
                      <Box
                        sx={{
                          mt: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <CircularProgress size={18} />
                        <Typography sx={{ color: "#6B7280" }}>
                          Uploading...
                        </Typography>
                      </Box>
                    )}
                    {!isKmlUploading && (kmlFile || formData.kmlFileName) && (
                      <Typography
                        sx={{ mt: 2, color: "#073706", fontWeight: 600 }}
                      >
                        ✓ File: {kmlFile?.name || formData.kmlFileName}
                      </Typography>
                    )}
                  </Box>

                  {/* KML Map Preview */}
                  {interventionKmlUrl && (
                    <Box sx={{ mt: 2, mb: 3 }}>
                      <Box
                        component="a"
                        href={interventionKmlUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: "#36B1A0",
                          fontWeight: 500,
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontSize: "0.9rem",
                          display: "inline-block",
                          mb: 1,
                        }}
                      >
                        View / Download Intervention KML File
                      </Box>
                      <Box
                        sx={{
                          border: "1px solid #E0E0E0",
                          borderRadius: "8px",
                          overflow: "hidden",
                          height: 400,
                        }}
                      >
                        <MapComponent kmlUrl={interventionKmlUrl} />
                      </Box>
                    </Box>
                  )}

                  {/* 2. Proposed Interventions */}
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, mb: 2, color: "#1F2937", mt: 4 }}
                  >
                    Proposed Interventions (Multiple selections permitted)
                  </Typography>

                  {/* Tree Plantation and Assisted Natural Regeneration */}
                  <Box
                    sx={{
                      mb: 4,
                      p: 2,
                      bgcolor: "#F9FAFB",
                      borderRadius: "8px",
                      border: "1px solid #E5E7EB",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, mb: 2, color: "#1F2937" }}
                    >
                      Tree Plantation and Assisted Natural Regeneration
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={styles.fieldLabel}>
                          Area Allocation (hectares)
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) &&
                            !formData.treePlantationArea
                          }
                          type="number"
                          placeholder="0.0"
                          value={formData.treePlantationArea || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "treePlantationArea",
                              Number(e.target.value)
                            )
                          }
                          sx={styles.inputField}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={styles.fieldLabel}>
                          Species Proposed (Native Species)*
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) &&
                            !formData.treePlantationSpecies
                          }
                          placeholder="Search or select from suggestions based on location"
                          value={formData.treePlantationSpecies || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "treePlantationSpecies",
                              e.target.value
                            )
                          }
                          sx={styles.inputField}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={styles.fieldLabel}>
                          Number of Seedlings per Hectare
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) &&
                            !formData.seedlingsPerHectare
                          }
                          type="number"
                          placeholder="e.g., 1600, 2500"
                          value={formData.seedlingsPerHectare || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "seedlingsPerHectare",
                              Number(e.target.value)
                            )
                          }
                          sx={styles.inputField}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={styles.fieldLabel}>
                          Spacing Pattern
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) && !formData.spacingPattern
                          }
                          placeholder="e.g., 2x2 meter, 3x3 meter"
                          value={formData.spacingPattern || ""}
                          onChange={(e) =>
                            handleInputChange("spacingPattern", e.target.value)
                          }
                          sx={styles.inputField}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={styles.fieldLabel}>
                          Nursery Source*
                        </Typography>
                        <FormControl
                          fullWidth
                          sx={styles.inputField}
                          error={
                            attemptedSteps.has(2) && !formData.nurserySource
                          }
                        >
                          <Select
                            required
                            value={formData.nurserySource || ""}
                            onChange={(e) =>
                              handleInputChange(
                                "nurserySource",
                                e.target.value as any
                              )
                            }
                          >
                            <MenuItem value="">Select...</MenuItem>
                            <MenuItem value="GOVERNMENT">
                              Government Nursery
                            </MenuItem>
                            <MenuItem value="PRIVATE">Private Nursery</MenuItem>
                            <MenuItem value="TEMPORARY">
                              Temporary Nursery (On-site)
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={styles.fieldLabel}>
                          Nursery Location
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) && !formData.nurseryLocation
                          }
                          placeholder="Address or coordinates"
                          value={formData.nurseryLocation || ""}
                          onChange={(e) =>
                            handleInputChange("nurseryLocation", e.target.value)
                          }
                          sx={styles.inputField}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography sx={styles.fieldLabel}>
                          Temporary Nursery Details (if applicable)
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) &&
                            !formData.temporaryNurseryDetails
                          }
                          placeholder="Details of temporary nurseries to be established in the land parcel"
                          value={formData.temporaryNurseryDetails || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "temporaryNurseryDetails",
                              e.target.value
                            )
                          }
                          multiline
                          sx={styles.inputField}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography sx={styles.fieldLabel}>
                          Maintenance Plan*
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) && !formData.maintenancePlan
                          }
                          placeholder="Describe maintenance activities, watering schedule, pest management, survival monitoring..."
                          value={formData.maintenancePlan || ""}
                          onChange={(e) =>
                            handleInputChange("maintenancePlan", e.target.value)
                          }
                          multiline
                          sx={styles.inputField}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Soil and Moisture Conservation Works */}
                  <Box
                    sx={{
                      mb: 4,
                      p: 2,
                      bgcolor: "#F9FAFB",
                      borderRadius: "8px",
                      border: "1px solid #E5E7EB",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, mb: 2, color: "#1F2937" }}
                    >
                      Soil and Moisture Conservation Works
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={styles.fieldLabel}>
                          Area Allocation (hectares)
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) &&
                            !formData.soilConservationArea
                          }
                          type="number"
                          placeholder="0.0"
                          value={formData.soilConservationArea || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "soilConservationArea",
                              Number(e.target.value)
                            )
                          }
                          sx={styles.inputField}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={styles.fieldLabel}>
                          Type of Works*
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) &&
                            !formData.conservationWorkType
                          }
                          placeholder="e.g., Contour trenching, Check dam, Gully plugging, Percolation tank"
                          value={formData.conservationWorkType || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "conservationWorkType",
                              e.target.value
                            )
                          }
                          sx={styles.inputField}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={styles.fieldLabel}>
                          Zone of Intervention (Linked to Map)
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) &&
                            !formData.conservationWorkZone
                          }
                          placeholder="Reference to zones defined in KML file"
                          value={formData.conservationWorkZone || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "conservationWorkZone",
                              e.target.value
                            )
                          }
                          sx={styles.inputField}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={styles.fieldLabel}>Purpose*</Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) &&
                            !formData.conservationWorkPurpose
                          }
                          placeholder="e.g., Moisture retention, Groundwater recharge, Erosion control"
                          value={formData.conservationWorkPurpose || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "conservationWorkPurpose",
                              e.target.value
                            )
                          }
                          sx={styles.inputField}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Water Supply and Irrigation Support */}
                  <Box
                    sx={{
                      mb: 4,
                      p: 2,
                      bgcolor: "#F9FAFB",
                      borderRadius: "8px",
                      border: "1px solid #E5E7EB",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, mb: 2, color: "#1F2937" }}
                    >
                      Water Supply and Irrigation Support
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={styles.fieldLabel}>
                          Area Allocation (hectares)
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) && !formData.waterSupplyArea
                          }
                          type="number"
                          placeholder="0.0"
                          value={formData.waterSupplyArea || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "waterSupplyArea",
                              Number(e.target.value)
                            )
                          }
                          sx={styles.inputField}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography sx={styles.fieldLabel}>
                          Proposed Water Source Development
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) &&
                            !formData.proposedWaterSource
                          }
                          placeholder="Describe proposed water source development, if any"
                          value={formData.proposedWaterSource || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "proposedWaterSource",
                              e.target.value
                            )
                          }
                          multiline
                          sx={styles.inputField}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography sx={styles.fieldLabel}>
                          Supply Line Layout
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) && !formData.supplyLineLayout
                          }
                          placeholder="Describe supply line layout within land parcel, if any"
                          value={formData.supplyLineLayout || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "supplyLineLayout",
                              e.target.value
                            )
                          }
                          multiline
                          sx={styles.inputField}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography sx={styles.fieldLabel}>
                          Storage / Water Harvesting Structures
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) &&
                            !formData.waterHarvestingStructures
                          }
                          placeholder="Describe storage or water harvesting structures, if any"
                          value={formData.waterHarvestingStructures || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "waterHarvestingStructures",
                              e.target.value
                            )
                          }
                          multiline
                          sx={styles.inputField}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Protection Measures */}
                  <Box
                    sx={{
                      mb: 4,
                      p: 2,
                      bgcolor: "#F9FAFB",
                      borderRadius: "8px",
                      border: "1px solid #E5E7EB",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, mb: 2, color: "#1F2937" }}
                    >
                      Protection Measures
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={styles.fieldLabel}>
                          Area Allocation (hectares)
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) && !formData.protectionArea
                          }
                          type="number"
                          placeholder="0.0"
                          value={formData.protectionArea || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "protectionArea",
                              Number(e.target.value)
                            )
                          }
                          sx={styles.inputField}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={styles.fieldLabel}>
                          Fencing Type*
                        </Typography>
                        <FormControl
                          fullWidth
                          sx={styles.inputField}
                          error={attemptedSteps.has(2) && !formData.fencingType}
                        >
                          <Select
                            required
                            value={formData.fencingType || ""}
                            onChange={(e) =>
                              handleInputChange(
                                "fencingType",
                                e.target.value as any
                              )
                            }
                          >
                            <MenuItem value="">Select...</MenuItem>
                            <MenuItem value="BARBED_WIRE">Barbed Wire</MenuItem>
                            <MenuItem value="STONE_WALL">Stone Wall</MenuItem>
                            <MenuItem value="WOODEN">Wooden</MenuItem>
                            <MenuItem value="MIXED">Mixed</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={styles.fieldLabel}>
                          Fencing Length (km)
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) && !formData.fencingLength
                          }
                          type="number"
                          placeholder="0.0"
                          value={formData.fencingLength || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "fencingLength",
                              Number(e.target.value)
                            )
                          }
                          sx={styles.inputField}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography sx={styles.fieldLabel}>
                          Fire Lines / Fire Prevention Works
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) &&
                            !formData.firePreventionWorks
                          }
                          placeholder="Describe fire prevention measures, fire line specifications, width and maintenance..."
                          value={formData.firePreventionWorks || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "firePreventionWorks",
                              e.target.value
                            )
                          }
                          multiline
                          sx={styles.inputField}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography sx={styles.fieldLabel}>
                          Watch and Ward Arrangements
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) &&
                            !formData.watchWardArrangements
                          }
                          placeholder="Describe watch and ward arrangements for site protection and security"
                          value={formData.watchWardArrangements || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "watchWardArrangements",
                              e.target.value
                            )
                          }
                          multiline
                          sx={styles.inputField}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Biodiversity and Ecosystem Enrichment */}
                  <Box
                    sx={{
                      mb: 4,
                      p: 2,
                      bgcolor: "#F9FAFB",
                      borderRadius: "8px",
                      border: "1px solid #E5E7EB",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, mb: 2, color: "#1F2937" }}
                    >
                      Biodiversity and Ecosystem Enrichment
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={styles.fieldLabel}>
                          Area Allocation (hectares)
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) && !formData.biodiversityArea
                          }
                          type="number"
                          placeholder="0.0"
                          value={formData.biodiversityArea || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "biodiversityArea",
                              Number(e.target.value)
                            )
                          }
                          sx={styles.inputField}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography sx={styles.fieldLabel}>
                          Inclusion of Pollinator Species
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) && !formData.pollinatorSpecies
                          }
                          placeholder="List pollinator species to be planted"
                          value={formData.pollinatorSpecies || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "pollinatorSpecies",
                              e.target.value
                            )
                          }
                          multiline
                          sx={styles.inputField}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography sx={styles.fieldLabel}>
                          NTFP (Non-Timber Forest Product) Yielding Species
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={attemptedSteps.has(2) && !formData.ntfpSpecies}
                          placeholder="List NTFP species to be included (fruits, honey, herbs, etc.)"
                          value={formData.ntfpSpecies || ""}
                          onChange={(e) =>
                            handleInputChange("ntfpSpecies", e.target.value)
                          }
                          multiline
                          sx={styles.inputField}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography sx={styles.fieldLabel}>
                          Grasses, Shrubs, and Medicinal Plants
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          error={
                            attemptedSteps.has(2) &&
                            !formData.grassesShrubsMedicinal
                          }
                          placeholder="List grasses, shrubs, and medicinal plants to be included"
                          value={formData.grassesShrubsMedicinal || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "grassesShrubsMedicinal",
                              e.target.value
                            )
                          }
                          multiline
                          sx={styles.inputField}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  {/* 3. Implementation Timeline */}
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, mb: 3, color: "#1F2937", mt: 4 }}
                  >
                    Implementation Timeline (Year 1 to Year 10)
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: "#6B7280" }}>
                    For each year, add targets by selecting an indicator,
                    choosing an activity based on that indicator, specifying
                    units and cost.
                  </Typography>
                  <Grid container spacing={3}>
                    {Array.from({ length: 10 }, (_, i) => {
                      const yearNumber = i + 1;
                      const fieldName =
                        `yearwiseTargetsData${yearNumber}` as keyof typeof formData;
                      const yearTargets = (formData[fieldName] ||
                        []) as TargetEntry[];

                      return (
                        <Grid item xs={12} key={`year-${yearNumber}`}>
                          <Box
                            sx={{
                              p: 2,
                              bgcolor: "#F9FAFB",
                              borderRadius: "8px",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2,
                              }}
                            >
                              <Typography
                                sx={{ fontWeight: 600, color: "#1F2937" }}
                              >
                                Year {yearNumber}
                              </Typography>
                              <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => addTarget(yearNumber)}
                                sx={{
                                  color: "#36B1A0",
                                  borderColor: "#36B1A0",
                                  "&:hover": {
                                    backgroundColor: "#E6F9F6",
                                  },
                                }}
                              >
                                Add Target
                              </Button>
                            </Box>

                            {yearTargets.length === 0 ? (
                              <Typography
                                variant="body2"
                                sx={{ color: "#9CA3AF", fontStyle: "italic" }}
                              >
                                No targets added yet. Click "Add Target" to
                                create one.
                              </Typography>
                            ) : (
                              <Grid container spacing={2}>
                                {yearTargets.map((target, tIdx) => (
                                  <Grid item xs={12} key={target.id || tIdx}>
                                    <Box
                                      sx={{
                                        p: 2,
                                        bgcolor: "#FFFFFF",
                                        borderRadius: "6px",
                                        border: "1px solid #D1D5DB",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "flex-start",
                                          mb: 2,
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontWeight: 600,
                                            color: "#1F2937",
                                            fontSize: "0.9rem",
                                          }}
                                        >
                                          Target {tIdx + 1}
                                        </Typography>
                                        <Button
                                          size="small"
                                          onClick={() =>
                                            removeTarget(yearNumber, target.id)
                                          }
                                          sx={{
                                            color: "#EF4444",
                                            minWidth: "auto",
                                            padding: "4px 8px",
                                          }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </Button>
                                      </Box>

                                      <Grid container spacing={2}>
                                        {/* Indicator Selection */}
                                        <Grid item xs={12} sm={6}>
                                          <Typography sx={styles.fieldLabel}>
                                            Indicator *
                                          </Typography>
                                          <FormControl fullWidth>
                                            <Select
                                              value={target.indicator || ""}
                                              onChange={(e) =>
                                                updateTarget(
                                                  yearNumber,
                                                  target.id,
                                                  "indicator",
                                                  e.target.value
                                                )
                                              }
                                              displayEmpty
                                              sx={styles.inputField}
                                            >
                                              <MenuItem value="" disabled>
                                                Select Indicator
                                              </MenuItem>
                                              {Object.values(IndicatorType).map(
                                                (ind) => (
                                                  <MenuItem
                                                    key={ind}
                                                    value={ind}
                                                  >
                                                    {ind}
                                                  </MenuItem>
                                                )
                                              )}
                                            </Select>
                                          </FormControl>
                                        </Grid>

                                        {/* Activity Selection (filtered by Indicator) */}
                                        <Grid item xs={12} sm={6}>
                                          <Typography sx={styles.fieldLabel}>
                                            Activity *
                                          </Typography>
                                          <FormControl fullWidth>
                                            <Select
                                              value={target.activity || ""}
                                              onChange={(e) =>
                                                updateTarget(
                                                  yearNumber,
                                                  target.id,
                                                  "activity",
                                                  e.target.value
                                                )
                                              }
                                              displayEmpty
                                              disabled={!target.indicator}
                                              sx={styles.inputField}
                                            >
                                              <MenuItem value="" disabled>
                                                {target.indicator
                                                  ? "Select Activity"
                                                  : "Select an Indicator first"}
                                              </MenuItem>
                                              {target.indicator &&
                                                INDICATOR_ACTIVITIES[
                                                  target.indicator as IndicatorType
                                                ]?.map((act) => (
                                                  <MenuItem
                                                    key={act}
                                                    value={act}
                                                  >
                                                    {act}
                                                  </MenuItem>
                                                ))}
                                            </Select>
                                          </FormControl>
                                        </Grid>

                                        {/* Unit */}
                                        <Grid item xs={12} sm={4}>
                                          <Typography sx={styles.fieldLabel}>
                                            Unit
                                          </Typography>
                                          <FormControl fullWidth>
                                            <Select
                                              value={target.unit || ""}
                                              onChange={(e) =>
                                                updateTarget(
                                                  yearNumber,
                                                  target.id,
                                                  "unit",
                                                  e.target.value
                                                )
                                              }
                                              displayEmpty
                                              sx={styles.inputField}
                                            >
                                              <MenuItem value="" disabled>
                                                Select Unit
                                              </MenuItem>
                                              {unitOptions.map((u) => (
                                                <MenuItem key={u} value={u}>
                                                  {u}
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                        </Grid>

                                        {/* Number of Units */}
                                        <Grid item xs={12} sm={4}>
                                          <Typography sx={styles.fieldLabel}>
                                            No. of Units
                                          </Typography>
                                          <TextField
                                            fullWidth
                                            type="number"
                                            placeholder="Enter quantity"
                                            value={
                                              target.numberOfUnits !== undefined
                                                ? target.numberOfUnits
                                                : ""
                                            }
                                            onChange={(e) =>
                                              updateTarget(
                                                yearNumber,
                                                target.id,
                                                "numberOfUnits",
                                                e.target.value
                                                  ? parseFloat(e.target.value)
                                                  : undefined
                                              )
                                            }
                                            sx={styles.inputField}
                                          />
                                        </Grid>

                                        {/* Description (optional) */}
                                        <Grid item xs={12} sm={8}>
                                          <Typography sx={styles.fieldLabel}>
                                            Description / Remarks
                                          </Typography>
                                          <TextField
                                            fullWidth
                                            placeholder="Optional description or remarks for this target"
                                            value={target.description || ""}
                                            onChange={(e) =>
                                              updateTarget(
                                                yearNumber,
                                                target.id,
                                                "description",
                                                e.target.value
                                              )
                                            }
                                            sx={styles.inputField}
                                          />
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  </Grid>
                                ))}
                              </Grid>
                            )}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Card>
            </fieldset>
          )}

          {/* Part D - Financial Outlay */}
          {activeStep === 3 && (
            <fieldset
              disabled={!canEditDpr}
              style={{ border: "none", padding: 0, margin: 0 }}
            >
              <Card sx={styles.card}>
                <Box sx={styles.sectionHeader}>
                  <Typography sx={styles.sectionTitle}>
                    Financial Outlay
                  </Typography>
                </Box>
                <Box sx={styles.sectionContent}>
                  <Typography variant="body2" sx={{ mb: 3, color: "#6B7280" }}>
                    Enter cost per unit and cost required for each target
                    defined in the Interventions & Zoning step.
                  </Typography>
                  <Grid container spacing={3}>
                    {Array.from({ length: 10 }, (_, i) => {
                      const yearNumber = i + 1;
                      const fieldName =
                        `yearwiseTargetsData${yearNumber}` as keyof typeof formData;
                      const yearTargets = (formData[fieldName] ||
                        []) as TargetEntry[];

                      if (yearTargets.length === 0) return null;

                      return (
                        <Grid item xs={12} key={`financial-year-${yearNumber}`}>
                          <Box
                            sx={{
                              p: 2,
                              bgcolor: "#F9FAFB",
                              borderRadius: "8px",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <Typography
                              sx={{ fontWeight: 600, color: "#1F2937", mb: 2 }}
                            >
                              Year {yearNumber}
                            </Typography>

                            <Grid container spacing={2}>
                              {yearTargets.map((target, tIdx) => (
                                <Grid item xs={12} key={target.id || tIdx}>
                                  <Box
                                    sx={{
                                      p: 2,
                                      bgcolor: "#FFFFFF",
                                      borderRadius: "6px",
                                      border: "1px solid #D1D5DB",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontWeight: 600,
                                        color: "#1F2937",
                                        fontSize: "0.9rem",
                                        mb: 1,
                                      }}
                                    >
                                      Target {tIdx + 1}:{" "}
                                      {target.indicator || "—"} /{" "}
                                      {target.activity || "—"}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "#6B7280", mb: 2 }}
                                    >
                                      Unit: {target.unit || "—"} | No. of Units:{" "}
                                      {target.numberOfUnits ?? "—"}
                                    </Typography>
                                    <Grid container spacing={2}>
                                      <Grid item xs={12} sm={4}>
                                        <Typography sx={styles.fieldLabel}>
                                          Cost Per Unit (₹)
                                        </Typography>
                                        <TextField
                                          fullWidth
                                          required
                                          type="number"
                                          placeholder="Enter cost per unit"
                                          value={
                                            target.costPerUnit !== undefined
                                              ? target.costPerUnit
                                              : ""
                                          }
                                          onChange={(e) =>
                                            updateTarget(
                                              yearNumber,
                                              target.id,
                                              "costPerUnit",
                                              e.target.value
                                                ? parseFloat(e.target.value)
                                                : undefined
                                            )
                                          }
                                          sx={styles.inputField}
                                        />
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        <Typography sx={styles.fieldLabel}>
                                          Cost Required (₹)
                                        </Typography>
                                        <TextField
                                          fullWidth
                                          type="number"
                                          placeholder="Auto-calculated"
                                          value={
                                            target.costRequired !== undefined
                                              ? target.costRequired
                                              : ""
                                          }
                                          InputProps={{
                                            readOnly: true,
                                          }}
                                          sx={{
                                            ...styles.inputField,
                                            "& .MuiInputBase-root": {
                                              backgroundColor: "#F3F4F6",
                                            },
                                          }}
                                        />
                                      </Grid>
                                    </Grid>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>

                            {/* Year Total */}
                            <Box
                              sx={{
                                mt: 2,
                                pt: 2,
                                borderTop: "1px solid #E5E7EB",
                                display: "flex",
                                justifyContent: "flex-end",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  color: "#1F2937",
                                  fontSize: "0.95rem",
                                }}
                              >
                                Year {yearNumber} Total Cost: ₹
                                {yearTargets
                                  .reduce(
                                    (sum, t) => sum + (t.costRequired || 0),
                                    0
                                  )
                                  .toLocaleString("en-IN")}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {/* Grand Total */}
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      bgcolor: "#E6F9F6",
                      borderRadius: "8px",
                      border: "1px solid #36B1A0",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: "#1F2937",
                        fontSize: "1.05rem",
                      }}
                    >
                      Grand Total: ₹
                      {Array.from({ length: 10 }, (_, i) => {
                        const fieldName = `yearwiseTargetsData${
                          i + 1
                        }` as keyof typeof formData;
                        const yearTargets = (formData[fieldName] ||
                          []) as TargetEntry[];
                        return yearTargets.reduce(
                          (sum, t) => sum + (t.costRequired || 0),
                          0
                        );
                      })
                        .reduce((a, b) => a + b, 0)
                        .toLocaleString("en-IN")}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </fieldset>
          )}

          {/* Part E - Declarations and Undertakings */}
          {activeStep === 4 && (
            <fieldset
              disabled={!canEditDeclarations}
              style={{ border: "none", padding: 0, margin: 0 }}
            >
              <Card sx={styles.card}>
                <Box sx={styles.sectionHeader}>
                  <Typography sx={styles.sectionTitle}>
                    Declarations and Undertakings
                  </Typography>
                </Box>
                <Box sx={styles.sectionContent}>
                  <Typography sx={{ mb: 3, color: "#1F2937", lineHeight: 1.6 }}>
                    The GCA shall submit a declaration as specified by the
                    Administrator for complying with the following conditions:
                  </Typography>

                  {/* Declaration 1 */}
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      bgcolor: "#F9FAFB",
                      borderRadius: "8px",
                      border:
                        attemptedSteps.has(4) &&
                        !formData.declarationTrueComplete
                          ? "2px solid #d32f2f"
                          : "1px solid #E5E7EB",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          required
                          checked={formData.declarationTrueComplete || false}
                          onChange={(e) =>
                            handleInputChange(
                              "declarationTrueComplete",
                              e.target.checked
                            )
                          }
                          sx={{ color: "#36B1A0" }}
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            sx={{ fontWeight: 600, color: "#1F2937", mb: 1 }}
                          >
                            Declaration of Truthfulness and Completeness
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#6B7280", lineHeight: 1.6 }}
                          >
                            All information furnished in this DPR is true,
                            complete, and correct to the best of its knowledge
                            and belief. Any misrepresentation or concealment of
                            material facts shall render this DPR liable for
                            rejection and cancellation of acknowledgment,
                            without prejudice to any other legal action.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>

                  {/* Declaration 2 */}
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      bgcolor: "#F9FAFB",
                      borderRadius: "8px",
                      border:
                        attemptedSteps.has(4) && !formData.declarationAbideRules
                          ? "2px solid #d32f2f"
                          : "1px solid #E5E7EB",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          required
                          checked={formData.declarationAbideRules || false}
                          onChange={(e) =>
                            handleInputChange(
                              "declarationAbideRules",
                              e.target.checked
                            )
                          }
                          sx={{ color: "#36B1A0" }}
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            sx={{ fontWeight: 600, color: "#1F2937", mb: 1 }}
                          >
                            Compliance with Green Credit Rules and Forest Acts
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#6B7280", lineHeight: 1.6 }}
                          >
                            The GCA shall abide by the provisions of the Green
                            Credit Rules, 2023, notifications, and guidelines
                            issued thereunder, as well as relevant provisions of
                            the State Forest Acts and the Van (Sanrakshan Evam
                            Samvardhan) Adhiniyam, 1980, wherever applicable.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>

                  {/* Declaration 3 */}
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      bgcolor: "#F9FAFB",
                      borderRadius: "8px",
                      border:
                        attemptedSteps.has(4) &&
                        !formData.declarationAdequateResources
                          ? "2px solid #d32f2f"
                          : "1px solid #E5E7EB",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          required
                          checked={
                            formData.declarationAdequateResources || false
                          }
                          onChange={(e) =>
                            handleInputChange(
                              "declarationAdequateResources",
                              e.target.checked
                            )
                          }
                          sx={{ color: "#36B1A0" }}
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            sx={{ fontWeight: 600, color: "#1F2937", mb: 1 }}
                          >
                            Adequate Manpower, Technical Expertise, and
                            Financial Resources
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#6B7280", lineHeight: 1.6 }}
                          >
                            The GCA shall ensure deployment of adequate
                            manpower, technical expertise, and financial
                            resources for effective and timely execution of the
                            activities specified in this DPR.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>

                  {/* Declaration 4 */}
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      bgcolor: "#F9FAFB",
                      borderRadius: "8px",
                      border:
                        attemptedSteps.has(4) &&
                        !formData.declarationMaintenanceFund
                          ? "2px solid #d32f2f"
                          : "1px solid #E5E7EB",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          required
                          checked={formData.declarationMaintenanceFund || false}
                          onChange={(e) =>
                            handleInputChange(
                              "declarationMaintenanceFund",
                              e.target.checked
                            )
                          }
                          sx={{ color: "#36B1A0" }}
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            sx={{ fontWeight: 600, color: "#1F2937", mb: 1 }}
                          >
                            Mandatory Maintenance Fund Deposit
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#6B7280", lineHeight: 1.6 }}
                          >
                            The GCA shall deposit the mandatory maintenance
                            fund, as specified by the Administrator, prior to
                            issuance of Green Credits.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>

                  {/* Declaration 5 */}
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      bgcolor: "#F9FAFB",
                      borderRadius: "8px",
                      border:
                        attemptedSteps.has(4) &&
                        !formData.declarationPerformanceParameters
                          ? "2px solid #d32f2f"
                          : "1px solid #E5E7EB",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          required
                          checked={
                            formData.declarationPerformanceParameters || false
                          }
                          onChange={(e) =>
                            handleInputChange(
                              "declarationPerformanceParameters",
                              e.target.checked
                            )
                          }
                          sx={{ color: "#36B1A0" }}
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            sx={{ fontWeight: 600, color: "#1F2937", mb: 1 }}
                          >
                            Compliance with Year-wise Performance Parameters
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#6B7280", lineHeight: 1.6 }}
                          >
                            The GCA shall comply with the year-wise performance
                            parameters, including survival rate, canopy density,
                            and number of trees in specified girth classes, as
                            detailed in Part D of this DPR.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>

                  {/* Declaration 6 */}
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      bgcolor: "#F9FAFB",
                      borderRadius: "8px",
                      border:
                        attemptedSteps.has(4) &&
                        !formData.declarationProgressReports
                          ? "2px solid #d32f2f"
                          : "1px solid #E5E7EB",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          required
                          checked={formData.declarationProgressReports || false}
                          onChange={(e) =>
                            handleInputChange(
                              "declarationProgressReports",
                              e.target.checked
                            )
                          }
                          sx={{ color: "#36B1A0" }}
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            sx={{ fontWeight: 600, color: "#1F2937", mb: 1 }}
                          >
                            Periodic Progress Reporting and Cooperation
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#6B7280", lineHeight: 1.6 }}
                          >
                            The GCA shall upload periodic progress reports on
                            the GCP Portal in the formats specified, and
                            cooperate with verification, evaluation, and
                            monitoring as specified by the Administrator.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>

                  {/* Declaration 7 */}
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      bgcolor: "#F9FAFB",
                      borderRadius: "8px",
                      border:
                        attemptedSteps.has(4) &&
                        !formData.declarationNoLandClaim
                          ? "2px solid #d32f2f"
                          : "1px solid #E5E7EB",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          required
                          checked={formData.declarationNoLandClaim || false}
                          onChange={(e) =>
                            handleInputChange(
                              "declarationNoLandClaim",
                              e.target.checked
                            )
                          }
                          sx={{ color: "#36B1A0" }}
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            sx={{ fontWeight: 600, color: "#1F2937", mb: 1 }}
                          >
                            No Claim on Land Rights
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#6B7280", lineHeight: 1.6 }}
                          >
                            The GCA agrees that it has no claim whatsoever or
                            any right on land including usufruct.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>

                  {/* Declaration 8 */}
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      bgcolor: "#F9FAFB",
                      borderRadius: "8px",
                      border:
                        attemptedSteps.has(4) &&
                        !formData.declarationNoCommercialActivity
                          ? "2px solid #d32f2f"
                          : "1px solid #E5E7EB",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          required
                          checked={
                            formData.declarationNoCommercialActivity || false
                          }
                          onChange={(e) =>
                            handleInputChange(
                              "declarationNoCommercialActivity",
                              e.target.checked
                            )
                          }
                          sx={{ color: "#36B1A0" }}
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            sx={{ fontWeight: 600, color: "#1F2937", mb: 1 }}
                          >
                            No Commercial Activity or Benefit
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#6B7280", lineHeight: 1.6 }}
                          >
                            The GCA shall not undertake any commercial activity
                            on the land or derive any commercial benefit
                            directly/indirectly from the land parcel which is
                            being used by the GCA except for the purpose
                            mentioned in the Memorandum of Understanding signed
                            between the State and the GCA.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                </Box>
              </Card>
            </fieldset>
          )}

          {/* Navigation Buttons */}
          <Card sx={styles.card}>
            <Box
              sx={{
                p: 3,
                display: "flex",
                gap: 2,
                justifyContent: "space-between",
              }}
            >
              <Button
                variant="outlined"
                onClick={handlePrevStep}
                sx={{
                  ...styles.button,
                  ...styles.secondaryButton,
                }}
                disabled={activeStep === 0}
              >
                Previous
              </Button>
              {canEditDpr &&
                (!dprStatus ||
                  dprStatus === "DPR_DRAFT" ||
                  dprStatus === "DPR_REJECTED") && (
                  <Button
                    variant="outlined"
                    sx={{
                      ...styles.button,
                      ...styles.secondaryButton,
                    }}
                    onClick={handleSaveAsDraft}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                    ) : null}
                    Save as Draft
                  </Button>
                )}
              {activeStep === steps.length - 1 ? (
                canEditDpr ? (
                  <Button
                    variant="contained"
                    sx={{
                      ...styles.button,
                      ...styles.primaryButton,
                    }}
                    onClick={() => {
                      setSubmitRemark("");
                      setSubmitDialogOpen(true);
                    }}
                    disabled={
                      isSubmitting ||
                      hasSubmitted ||
                      !formData.declarationTrueComplete ||
                      !formData.declarationAbideRules ||
                      !formData.declarationAdequateResources ||
                      !formData.declarationMaintenanceFund ||
                      !formData.declarationPerformanceParameters ||
                      !formData.declarationProgressReports ||
                      !formData.declarationNoLandClaim ||
                      !formData.declarationNoCommercialActivity
                    }
                  >
                    {isSubmitting ? (
                      <CircularProgress
                        size={20}
                        sx={{ mr: 1, color: "#fff" }}
                      />
                    ) : null}
                    Submit DPR
                  </Button>
                ) : null
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNextStep}
                  sx={{
                    ...styles.button,
                    ...styles.primaryButton,
                  }}
                >
                  Next
                </Button>
              )}
            </Box>
          </Card>

          {/* DPR Query History */}
          <StatusHistory
            title="DPR History"
            records={dprQueryHistory}
            responseLabel="Remark Added"
            maxWidth="100%"
            sx={{ margin: "0 0 24px" }}
          />

          {/* DPR Review Actions (Approve / Query) */}
          {showReviewActions && (
            <Card sx={styles.card}>
              <Box
                sx={{
                  p: 3,
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ mr: "auto", fontWeight: 600 }}
                >
                  Review Actions
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  disabled={isReviewLoading}
                  onClick={() => {
                    setApproveReason("");
                    setApproveDialogOpen(true);
                  }}
                  sx={{ textTransform: "none" }}
                >
                  {isReviewLoading ? (
                    <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                  ) : null}
                  {isSNO ? "Forward to Admin" : "Approve"}
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  disabled={isReviewLoading}
                  onClick={() => setQueryDialogOpen(true)}
                  sx={{ textTransform: "none" }}
                >
                  Raise Query
                </Button>
              </Box>
            </Card>
          )}

          {/* Submit DPR */}
          <Dialog
            open={submitDialogOpen}
            onClose={() => setSubmitDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
              Submit DPR
            </DialogTitle>
            <DialogContent sx={{ pt: "8px !important" }}>
              <Typography
                variant="body2"
                sx={{ mb: 2.5, color: "#1F2937", lineHeight: 1.6 }}
              >
                Once submitted, the DPR goes to the District Nodal Officer for
                review and you will not be able to edit it. Add a remark
                describing this submission.
              </Typography>
              {/* The theme pins MuiInputBase-root to height: 40px, which a multiline
                  field must override or the textarea overflows its own box. */}
              <FormControl fullWidth>
                <InputLabel
                  shrink
                  htmlFor="dpr-submit-remark"
                  sx={{ position: "static", mb: 1 }}
                >
                  Submission remark
                </InputLabel>
                <InputBase
                  id="dpr-submit-remark"
                  autoFocus
                  multiline
                  rows={3}
                  placeholder="Type your remark here..."
                  value={submitRemark}
                  onChange={(e) => setSubmitRemark(e.target.value)}
                  sx={{ height: "auto", alignItems: "flex-start" }}
                />
              </FormControl>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
              <Button
                onClick={() => setSubmitDialogOpen(false)}
                sx={{ textTransform: "none" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="success"
                disabled={!submitRemark.trim() || isSubmitting || hasSubmitted}
                onClick={() => handleSubmit(submitRemark)}
                sx={{ textTransform: "none" }}
              >
                {isSubmitting ? (
                  <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                ) : null}
                Confirm Submit
              </Button>
            </DialogActions>
          </Dialog>

          {/* Approve / Forward to Admin */}
          <Dialog
            open={approveDialogOpen}
            onClose={() => setApproveDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
              {isSNO ? "Forward DPR to Admin" : "Approve DPR"}
            </DialogTitle>
            <DialogContent sx={{ pt: "8px !important" }}>
              <Typography
                variant="body2"
                sx={{ mb: 2.5, color: "#1F2937", lineHeight: 1.6 }}
              >
                {isSNO
                  ? "This forwards the DPR to Admin for review and cannot be undone."
                  : "This approves the DPR and cannot be undone."}{" "}
                Add a remark explaining the decision.
              </Typography>
              {/* The theme pins MuiInputBase-root to height: 40px, which a multiline
                  field must override or the textarea overflows its own box. */}
              <FormControl fullWidth>
                <InputLabel
                  shrink
                  htmlFor="dpr-approve-reason"
                  sx={{ position: "static", mb: 1 }}
                >
                  {isSNO ? "Reason for forwarding" : "Approval remark"}
                </InputLabel>
                <InputBase
                  id="dpr-approve-reason"
                  autoFocus
                  multiline
                  rows={3}
                  placeholder="Type your remark here..."
                  value={approveReason}
                  onChange={(e) => setApproveReason(e.target.value)}
                  sx={{ height: "auto", alignItems: "flex-start" }}
                />
              </FormControl>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
              <Button
                onClick={() => setApproveDialogOpen(false)}
                sx={{ textTransform: "none" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="success"
                disabled={!approveReason.trim() || isReviewLoading}
                onClick={() =>
                  reviewMutate({
                    epid: landOwnershipEpid,
                    action: "APPROVE",
                    reason: approveReason.trim(),
                  })
                }
                sx={{ textTransform: "none" }}
              >
                {isReviewLoading ? (
                  <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                ) : null}
                {isSNO ? "Confirm Forward" : "Confirm Approve"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Query Dialog */}
          <Dialog
            open={queryDialogOpen}
            onClose={() => setQueryDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Raise Query</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                // label="Query / Remark"
                fullWidth
                // multiline
                // rows={1}
                value={queryText}
                onChange={(e) => setQueryText(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setQueryDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                disabled={!queryText.trim() || isReviewLoading}
                onClick={() =>
                  setConfirmDialog({
                    open: true,
                    title: "Submit Query",
                    message: "Are you sure you want to raise this query?",
                    onConfirm: () => {
                      setConfirmDialog((prev) => ({ ...prev, open: false }));
                      reviewMutate({
                        epid: landOwnershipEpid,
                        action: "QUERY",
                        query: queryText.trim(),
                      });
                    },
                  })
                }
              >
                Submit Query
              </Button>
            </DialogActions>
          </Dialog>

          {/* Confirmation Dialog */}
          <Dialog
            open={confirmDialog.open}
            onClose={() =>
              setConfirmDialog((prev) => ({ ...prev, open: false }))
            }
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>{confirmDialog.title}</DialogTitle>
            <DialogContent>
              <Typography>{confirmDialog.message}</Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() =>
                  setConfirmDialog((prev) => ({ ...prev, open: false }))
                }
              >
                Cancel
              </Button>
              <Button variant="contained" onClick={confirmDialog.onConfirm}>
                Confirm
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar for success/error messages */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={5000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity={snackbar.severity}
              variant="filled"
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </>
      )}
    </Container>
  );
};

export default DPRForm;
